(function () {
  var lastToastAt = 0;
  var klaviyoReviewSummaryCache = {};
  var klaviyoProductRatingsCache = {};
  var klaviyoProductRatingsRequestCache = {};
  var tallOwlRotatingScentIndexCache = {};

  // Klaviyo form WF7GV3 is an orphaned live version whose parent is no longer
  // available in the Klaviyo UI/API. Suppress only that stale BOGO Free popup
  // while the vendor support case is open; keep onsite tracking, reviews,
  // consent records, and every other form intact.
  function initStaleKlaviyoFormContainment() {
    var staleFormId = 'WF7GV3';
    var staleFormSelector = '[data-testid="klaviyo-form-' + staleFormId + '"]';

    function suppressStaleForm() {
      var staleForm = document.querySelector(staleFormSelector);
      if (!staleForm) return false;

      var dialog = staleForm.closest('[role="dialog"]');
      if (dialog) {
        dialog.remove();
      } else {
        staleForm.remove();
      }

      return true;
    }

    window.addEventListener('klaviyoForms', function (event) {
      var detail = event && event.detail ? event.detail : {};
      if (detail.formId !== staleFormId || detail.type !== 'open') return;

      var body = document.body;
      var originalBodyState = body ? {
        hadScrollLockClass: body.classList.contains('klaviyo-prevent-body-scrolling'),
        overflow: body.style.overflow,
        overflowY: body.style.overflowY
      } : null;

      function hasAnotherKlaviyoForm() {
        var forms = document.querySelectorAll('[data-testid^="klaviyo-form-"]');
        for (var index = 0; index < forms.length; index += 1) {
          if (!forms[index].matches(staleFormSelector)) return true;
        }
        return false;
      }

      function restoreBodyScrollIfSafe() {
        if (!body || !originalBodyState) return;
        if (document.querySelector(staleFormSelector) || hasAnotherKlaviyoForm()) return;

        if (
          !originalBodyState.hadScrollLockClass &&
          body.classList.contains('klaviyo-prevent-body-scrolling')
        ) {
          body.classList.remove('klaviyo-prevent-body-scrolling');
        }
        if (body.style.overflow === 'hidden' && originalBodyState.overflow !== 'hidden') {
          body.style.overflow = originalBodyState.overflow;
        }
        if (body.style.overflowY === 'hidden' && originalBodyState.overflowY !== 'hidden') {
          body.style.overflowY = originalBodyState.overflowY;
        }
      }

      // Klaviyo emits its open event before inserting the popup, then applies
      // the body scroll lock after insertion. Watch both phases briefly so
      // removing this one orphaned form can never leave the storefront locked.
      var formObserver = new MutationObserver(function () {
        suppressStaleForm();
        restoreBodyScrollIfSafe();
      });
      formObserver.observe(document.documentElement, {
        childList: true,
        subtree: true
      });

      var bodyObserver = body ? new MutationObserver(function () {
        restoreBodyScrollIfSafe();
      }) : null;
      if (bodyObserver) {
        bodyObserver.observe(body, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }

      suppressStaleForm();
      restoreBodyScrollIfSafe();

      window.setTimeout(function () {
        suppressStaleForm();
        restoreBodyScrollIfSafe();
        formObserver.disconnect();
        if (bodyObserver) bodyObserver.disconnect();
      }, 3000);
    });
  }

  initStaleKlaviyoFormContainment();

  // --- Global serialized cart-write queue ---
  // Shopify's cart AJAX API (/cart/add|update|change.js) races/drops writes when
  // several fire at once. The promo flag-writer, the free-gift picker, and the
  // BOGO auto-add all mutate the cart on the same events, so every cart write
  // goes through this one chain to take turns - otherwise a collision silently
  // drops the free 2nd unit or the gift (the "not added until refresh" bug).
  var tallOwlCartWriteChain = Promise.resolve();
  function tallOwlQueueCartWrite(task) {
    var result = tallOwlCartWriteChain.then(task, task);
    tallOwlCartWriteChain = result.then(function () {}, function () {});
    return result;
  }
  // Set true when the shopper clicks "View cart" so the deferred gift scent
  // popup does not flash open as we navigate away to the cart.
  var tallOwlSuppressGiftPopup = false;

  function readStorageValue(storage, key) {
    if (!storage) return '';
    try {
      return storage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  function writeStorageValue(storage, key, value) {
    if (!storage) return;
    try {
      storage.setItem(key, value);
    } catch (error) {
      /* Ignore storage write issues */
    }
  }

  function getReviewSummaryCompanyId(companyId) {
    var trimmed = String(companyId || '').trim();
    return trimmed || 'UJMPJV';
  }

  function buildReviewSummaryUrl(companyId, subtype) {
    var params = new URLSearchParams();
    params.set('product_id', 'all');
    params.set('company_id', companyId);
    params.set('limit', '1');
    params.set('offset', '0');
    params.set('sort', '0');
    params.set('filter', '');
    params.set('type', 'reviews');
    params.set('media', 'false');
    if (subtype) params.set('subtype', subtype);
    return 'https://fast.a.klaviyo.com/reviews/api/client_reviews/all/?' + params.toString();
  }

  async function fetchSummaryPart(companyId, subtype) {
    var response = await fetch(buildReviewSummaryUrl(companyId, subtype), { method: 'GET' });
    if (!response.ok) throw new Error('Failed to fetch Klaviyo review summary');
    var payload = await response.json();
    var summary = payload && payload.summary ? payload.summary : null;
    if (!summary) return null;

    var count = Number(summary.review_count);
    var rating = Number(summary.star_rating);
    if (!Number.isFinite(count) || count <= 0 || !Number.isFinite(rating)) return null;

    return {
      count: Math.round(count),
      rating: rating
    };
  }

  function combineSummaryParts(parts) {
    var valid = (parts || []).filter(function (part) {
      return part && part.count > 0;
    });
    if (!valid.length) return null;

    var totalCount = 0;
    var weightedRatingTotal = 0;
    valid.forEach(function (part) {
      totalCount += part.count;
      weightedRatingTotal += part.rating * part.count;
    });
    if (!totalCount) return null;

    return {
      count: totalCount,
      rating: weightedRatingTotal / totalCount
    };
  }

  function getSharedKlaviyoReviewSummary(companyId) {
    var safeCompanyId = getReviewSummaryCompanyId(companyId);
    if (klaviyoReviewSummaryCache[safeCompanyId]) {
      return klaviyoReviewSummaryCache[safeCompanyId];
    }

    klaviyoReviewSummaryCache[safeCompanyId] = Promise.all([
      fetchSummaryPart(safeCompanyId, ''),
      fetchSummaryPart(safeCompanyId, 'store')
    ])
      .then(function (parts) {
        return combineSummaryParts(parts);
      })
      .catch(function (error) {
        delete klaviyoReviewSummaryCache[safeCompanyId];
        throw error;
      });

    return klaviyoReviewSummaryCache[safeCompanyId];
  }

  function getCompanyRatingsCache(companyId) {
    var safeCompanyId = getReviewSummaryCompanyId(companyId);
    if (!klaviyoProductRatingsCache[safeCompanyId]) {
      klaviyoProductRatingsCache[safeCompanyId] = {};
    }
    return klaviyoProductRatingsCache[safeCompanyId];
  }

  function getRequestCacheKey(companyId, productIds) {
    return getReviewSummaryCompanyId(companyId) + '::' + productIds.slice().sort().join(',');
  }

  function fetchKlaviyoProductRatings(companyId, productIds) {
    var safeCompanyId = getReviewSummaryCompanyId(companyId);
    var uniqueIds = Array.from(
      new Set(
        (productIds || [])
          .map(function (id) {
            return String(id || '').trim();
          })
          .filter(function (id) {
            return id.length > 0;
          })
      )
    );
    if (!uniqueIds.length) return Promise.resolve({});

    var companyCache = getCompanyRatingsCache(safeCompanyId);
    var missingIds = uniqueIds.filter(function (id) {
      return !Object.prototype.hasOwnProperty.call(companyCache, id);
    });

    function buildResult() {
      var result = {};
      uniqueIds.forEach(function (id) {
        if (Object.prototype.hasOwnProperty.call(companyCache, id)) {
          result[id] = companyCache[id];
        }
      });
      return result;
    }

    if (!missingIds.length) {
      return Promise.resolve(buildResult());
    }

    var requestKey = getRequestCacheKey(safeCompanyId, missingIds);
    if (!klaviyoProductRatingsRequestCache[requestKey]) {
      var params = new URLSearchParams();
      params.set('company_id', safeCompanyId);
      params.set('products', JSON.stringify(missingIds));

      klaviyoProductRatingsRequestCache[requestKey] = fetch(
        'https://fast.a.klaviyo.com/reviews/api/client_reviews/?' + params.toString(),
        { method: 'GET' }
      )
        .then(function (response) {
          if (!response.ok) throw new Error('Unable to load Klaviyo product ratings');
          return response.json();
        })
        .then(function (payload) {
          var products = payload && Array.isArray(payload.products) ? payload.products : [];
          var found = {};

          products.forEach(function (product) {
            if (!product || product.shopify_id == null) return;
            var id = String(product.shopify_id);
            var rating = Number(product.star_rating);
            var count = Number(product.review_count);
            if (!Number.isFinite(rating)) return;

            found[id] = true;
            companyCache[id] = {
              rating: Math.round(rating * 10) / 10,
              count: Number.isFinite(count) && count > 0 ? Math.round(count) : 0
            };
          });

          missingIds.forEach(function (id) {
            if (!found[id]) {
              companyCache[id] = null;
            }
          });
        })
        .finally(function () {
          delete klaviyoProductRatingsRequestCache[requestKey];
        });
    }

    return klaviyoProductRatingsRequestCache[requestKey].then(function () {
      return buildResult();
    });
  }

  window.tallOwlReviews = window.tallOwlReviews || {};
  window.tallOwlReviews.getSummary = getSharedKlaviyoReviewSummary;
  window.tallOwlReviews.getProductRatings = fetchKlaviyoProductRatings;
  window.dispatchEvent(new CustomEvent('tallowl:reviews-helper-ready'));

  function collectGridRatingElements(scope) {
    var elements = [];
    if (!scope) return elements;

    if (scope.nodeType === 1 && scope.matches('[data-klaviyo-grid-rating][data-klaviyo-product-id]')) {
      elements.push(scope);
    }

    if (scope.querySelectorAll) {
      elements = elements.concat(
        Array.prototype.slice.call(scope.querySelectorAll('[data-klaviyo-grid-rating][data-klaviyo-product-id]'))
      );
    }

    return elements;
  }

  function applyGridRating(el, ratingData) {
    var valueEl = el.querySelector('.tall-owl-grid-rating__value');
    var countEl = el.querySelector('.tall-owl-grid-rating__count');
    if (!valueEl || !countEl || !ratingData) return;

    var rating = Number(ratingData.rating);
    var safeCount = Number(ratingData.count);

    // No real reviews yet (new products): keep the stars row hidden entirely.
    if (!Number.isFinite(rating) || rating <= 0 || !Number.isFinite(safeCount) || safeCount <= 0) {
      el.hidden = true;
      return;
    }

    var normalizedRating = (Math.round(rating * 10) / 10).toFixed(1);
    var formattedCount = Math.round(safeCount).toLocaleString();

    valueEl.textContent = normalizedRating;
    var reviewLabel = Math.round(safeCount) === 1 ? 'review' : 'reviews';
    countEl.textContent = formattedCount + ' ' + reviewLabel;
    el.setAttribute(
      'aria-label',
      'Rated ' + normalizedRating + ' out of 5 from ' + formattedCount + ' ' + reviewLabel
    );
    el.hidden = false;
  }

  function initCollectionGridRatings(scope) {
    if (!document.body.classList.contains('template-collection')) return;

    var root = scope || document;
    var ratingEls = collectGridRatingElements(root).filter(function (el) {
      return el.getAttribute('data-klaviyo-grid-rating-ready') !== 'true';
    });
    if (!ratingEls.length) return;

    var companyId = 'UJMPJV';
    for (var i = 0; i < ratingEls.length; i += 1) {
      var configuredCompanyId = (ratingEls[i].getAttribute('data-klaviyo-company-id') || '').trim();
      if (configuredCompanyId) {
        companyId = configuredCompanyId;
        break;
      }
    }

    var productIds = ratingEls
      .map(function (el) {
        return (el.getAttribute('data-klaviyo-product-id') || '').trim();
      })
      .filter(function (id) {
        return id.length > 0;
      });
    if (!productIds.length) return;

    fetchKlaviyoProductRatings(companyId, productIds)
      .then(function (ratingsById) {
        ratingEls.forEach(function (el) {
          var productId = (el.getAttribute('data-klaviyo-product-id') || '').trim();
          if (productId && ratingsById && ratingsById[productId]) {
            applyGridRating(el, ratingsById[productId]);
          }
          el.setAttribute('data-klaviyo-grid-rating-ready', 'true');
        });
      })
      .catch(function () {
        ratingEls.forEach(function (el) {
          el.setAttribute('data-klaviyo-grid-rating-ready', 'true');
        });
      });
  }

  function initCollectionGridRatingsObserver() {
    if (!document.body.classList.contains('template-collection') || !window.MutationObserver) return;
    var container = document.getElementById('CollectionAjaxContent') || document.getElementById('MainContent');
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (!node || node.nodeType !== 1) return;
          initCollectionGridRatings(node);
          initRotatingScentCards(node);
          initCollectionCardForms(node);
        });
      });
    });
    observer.observe(container, { childList: true, subtree: true });
  }

  function initCollectionCardForms(scope) {
    if (!document.body.classList.contains('template-collection')) return;

    var root = scope || document;
    var forms = [];
    if (root.nodeType === 1 && root.matches('[data-tall-owl-collection-card-form]')) {
      forms.push(root);
    }
    if (root.querySelectorAll) {
      forms = forms.concat(Array.prototype.slice.call(root.querySelectorAll('[data-tall-owl-collection-card-form]')));
    }

    forms.forEach(function (form) {
      if (form.getAttribute('data-tall-owl-collection-card-bound') === 'true') return;
      form.setAttribute('data-tall-owl-collection-card-bound', 'true');

      var select = form.querySelector('select[name="id"]');
      var button = form.querySelector('.tall-owl-collection-card-form__button');
      var card = form.closest('.grid-product');
      var priceEl = card ? card.querySelector('[data-tall-owl-card-price]') : null;

      function syncSelectedVariant() {
        if (!select) return;
        var option = select.options[select.selectedIndex];
        if (!option) return;

        if (priceEl && option.getAttribute('data-price')) {
          priceEl.textContent = option.getAttribute('data-price');
        }

        if (button && !button.hasAttribute('data-adding')) {
          var available = option.getAttribute('data-available') !== 'false' && !option.disabled;
          button.disabled = !available;
          button.textContent = available ? 'Add' : 'Sold out';
        }
      }

      if (select) {
        select.addEventListener('change', syncSelectedVariant);
        syncSelectedVariant();
      }

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (!button || button.disabled || button.hasAttribute('data-adding')) return;

        var formData = new FormData(form);
        var variantId = formData.get('id');
        if (!variantId) return;

        var previousText = button.textContent || 'Add';
        button.disabled = true;
        button.setAttribute('data-adding', 'true');
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding...';

        addToCart(variantId, 1)
          .then(function () {
            button.textContent = 'Added';
            return new Promise(function (resolve) {
              window.setTimeout(resolve, 450);
            });
          })
          .then(function () {
            button.removeAttribute('data-adding');
            button.removeAttribute('aria-busy');
            syncSelectedVariant();
            if (!select) {
              button.disabled = false;
              button.textContent = previousText;
            }
          })
          .catch(function () {
            button.textContent = 'Try again';
            window.setTimeout(function () {
              button.removeAttribute('data-adding');
              button.removeAttribute('aria-busy');
              syncSelectedVariant();
              if (!select) {
                button.disabled = false;
                button.textContent = previousText;
              }
            }, 1200);
          });
      });
    });
  }

  function collectVariantDescriptionBlocks(scope) {
    var blocks = [];
    if (!scope) return blocks;

    if (scope.nodeType === 1 && scope.matches('[data-variant-description-block]')) {
      blocks.push(scope);
    }

    if (scope.querySelectorAll) {
      blocks = blocks.concat(
        Array.prototype.slice.call(scope.querySelectorAll('[data-variant-description-block]'))
      );
    }

    return blocks;
  }

  function getCurrentVariantIdForSection(productSection) {
    if (!productSection || !productSection.querySelector) return '';
    var currentVariantNode = productSection.querySelector('[data-current-variant-json]');
    if (!currentVariantNode) return '';

    var currentVariant = parseJsonData((currentVariantNode.textContent || '').trim());
    if (!currentVariant || currentVariant.id == null) return '';
    return String(currentVariant.id);
  }

  function renderVariantDescriptionForVariant(block, variantId) {
    if (!block || !block.querySelector) return;

    var output = block.querySelector('[data-variant-description-output]');
    if (!output) return;

    var map = block._tallOwlVariantDescriptionMap || {};
    var optionOneMap = block._tallOwlVariantDescriptionOptionOneMap || {};
    var variantOptionOneMap = block._tallOwlVariantDescriptionVariantOptionOneMap || {};
    var productFallbackHtml = block._tallOwlVariantDescriptionProductFallbackHtml || '';
    var fallbackHtml = block._tallOwlVariantDescriptionFallbackHtml || '';
    var variantKey = variantId == null ? '' : String(variantId);
    var nextHtml = '';
    var optionOneKey = '';

    if (variantKey && Object.prototype.hasOwnProperty.call(map, variantKey)) {
      nextHtml = map[variantKey] || '';
    }

    if (!nextHtml && variantKey && Object.prototype.hasOwnProperty.call(variantOptionOneMap, variantKey)) {
      optionOneKey = variantOptionOneMap[variantKey] || '';
      if (optionOneKey && Object.prototype.hasOwnProperty.call(optionOneMap, optionOneKey)) {
        nextHtml = optionOneMap[optionOneKey] || '';
      }
    }

    if (!nextHtml) {
      nextHtml = productFallbackHtml;
    }

    if (!nextHtml) {
      nextHtml = fallbackHtml;
    }

    if (!isProductDescriptionSafe(nextHtml)) {
      nextHtml = fallbackHtml;
    }

    output.innerHTML = nextHtml || '';

    var isEmpty = !(nextHtml && String(nextHtml).replace(/\s+/g, '').length);
    if (isEmpty) {
      block.setAttribute('data-variant-description-empty', 'true');
    } else {
      block.removeAttribute('data-variant-description-empty');
    }
  }

  function isProductDescriptionSafe(html) {
    var blockedTerms = [
      'acne',
      'cure',
      'cured',
      'dermatitis',
      'eczema',
      'heal',
      'healed',
      'healing',
      'inflammation',
      'inflammatory',
      'inflamed',
      'irritation',
      'psoriasis',
      'rash',
      'restores',
      'rosacea',
      'sensitive skin',
      'steroid',
      'treat',
      'treated',
      'treatment',
      'wound'
    ];
    var container = document.createElement('div');
    container.innerHTML = String(html || '');
    var text = (container.textContent || container.innerText || '').toLowerCase();
    if (!text) return true;

    return !blockedTerms.some(function (term) {
      return text.indexOf(term) !== -1;
    });
  }

  function initVariantDescriptionBlocks(scope) {
    var blocks = collectVariantDescriptionBlocks(scope);
    if (!blocks.length) return;

    blocks.forEach(function (block) {
      if (block.getAttribute('data-variant-description-ready') === 'true') return;

      var mapNode = block.querySelector('[data-variant-description-map]');
      var optionOneMapNode = block.querySelector('[data-variant-description-option-one-map]');
      var variantOptionOneMapNode = block.querySelector('[data-variant-description-variant-option-one-map]');
      var fallbackNode = block.querySelector('template[data-variant-description-fallback]');
      var productFallbackNode = block.querySelector('template[data-variant-description-product-fallback]');
      var productSection = block.closest('.product-section');
      if (!mapNode || !productSection) return;

      var mapData = parseJsonData((mapNode.textContent || '').trim());
      var optionOneMapData = optionOneMapNode ? parseJsonData((optionOneMapNode.textContent || '').trim()) : null;
      var variantOptionOneMapData = variantOptionOneMapNode ? parseJsonData((variantOptionOneMapNode.textContent || '').trim()) : null;
      block._tallOwlVariantDescriptionMap = mapData && typeof mapData === 'object' ? mapData : {};
      block._tallOwlVariantDescriptionOptionOneMap = optionOneMapData && typeof optionOneMapData === 'object' ? optionOneMapData : {};
      block._tallOwlVariantDescriptionVariantOptionOneMap = variantOptionOneMapData && typeof variantOptionOneMapData === 'object' ? variantOptionOneMapData : {};
      block._tallOwlVariantDescriptionProductFallbackHtml = productFallbackNode ? productFallbackNode.innerHTML : '';
      block._tallOwlVariantDescriptionFallbackHtml = fallbackNode ? fallbackNode.innerHTML : '';

      var initialVariantId = (block.getAttribute('data-current-variant-id') || '').trim();
      if (!initialVariantId) {
        initialVariantId = getCurrentVariantIdForSection(productSection);
      }

      renderVariantDescriptionForVariant(block, initialVariantId);

      var onVariantChange = function (event) {
        if (!block.isConnected) {
          productSection.removeEventListener('variantChange', onVariantChange);
          return;
        }

        var variant = event && event.detail && event.detail.variant ? event.detail.variant : null;
        var variantId = variant && variant.id != null ? String(variant.id) : '';
        renderVariantDescriptionForVariant(block, variantId);
      };

      productSection.addEventListener('variantChange', onVariantChange);

      block.setAttribute('data-variant-description-ready', 'true');
    });
  }

  function initVariantDescriptionObserver() {
    if (!window.MutationObserver) return;
    var container = document.getElementById('MainContent') || document.body;
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (!node || node.nodeType !== 1) return;
          initVariantDescriptionBlocks(node);
        });
      });
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  function parseJsonData(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function getSecureRandomInt(maxExclusive) {
    if (!Number.isFinite(maxExclusive) || maxExclusive <= 0) return 0;
    var max = Math.floor(maxExclusive);
    if (max <= 1) return 0;

    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      var randomBuffer = new Uint32Array(1);
      window.crypto.getRandomValues(randomBuffer);
      return randomBuffer[0] % max;
    }

    return Math.floor(Math.random() * max);
  }

  function getRotatingScentIndex(productId, variantCount) {
    if (variantCount < 2) return 0;
    var cacheKey = productId || 'unknown';
    if (Object.prototype.hasOwnProperty.call(tallOwlRotatingScentIndexCache, cacheKey)) {
      return tallOwlRotatingScentIndexCache[cacheKey];
    }
    var randomIndex = getSecureRandomInt(variantCount);
    tallOwlRotatingScentIndexCache[cacheKey] = randomIndex;
    return randomIndex;
  }

  function buildResponsiveSrcset(imageUrl) {
    if (!/[?&]width=\d+/.test(imageUrl)) return '';
    return [200, 280, 420, 560]
      .map(function (w) {
        return imageUrl.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w';
      })
      .join(', ');
  }

  function updateCardImageForVariant(card, imageUrl) {
    if (!imageUrl) return;

    var primaryImage = card.querySelector(
      '.grid__item-image-wrapper .grid__image-ratio img, .grid__item-image-wrapper .image-wrap img, .tall-owl-product-card__image-link img, .tall-owl-product-card__image'
    );
    if (!primaryImage) return;

    // Keep the swap responsive: a single fixed-width srcset forces oversized
    // downloads on mobile (flagged by Lighthouse), so rebuild the width ladder.
    var responsiveSrcset = buildResponsiveSrcset(imageUrl) || imageUrl;
    primaryImage.setAttribute('src', imageUrl);
    primaryImage.setAttribute('srcset', responsiveSrcset);
    primaryImage.setAttribute('data-src', imageUrl);
    primaryImage.setAttribute('data-srcset', responsiveSrcset);
  }

  function updateCardLinksForVariant(card, variantUrl) {
    if (!variantUrl) return;
    var links = card.querySelectorAll('[data-tall-owl-random-scent-link]');
    Array.prototype.forEach.call(links, function (link) {
      link.setAttribute('href', variantUrl);
    });
  }

  function updateScentLabel(card, scentLabel) {
    if (!scentLabel) return;
    var note = card.querySelector('[data-tall-owl-scent-note]');
    if (!note) return;

    var scentCount = Number(note.getAttribute('data-scent-count'));
    var countText = Number.isFinite(scentCount) && scentCount > 1 ? String(Math.round(scentCount)) + ' scents' : '';
    var scentValueLabel = note.querySelector('[data-tall-owl-current-scent-value]');
    var countLabel = note.querySelector('[data-tall-owl-scent-count-label]');
    var currentLabel = note.querySelector('[data-tall-owl-current-scent-label]');
    var currentText = 'Showing ' + scentLabel;

    if (countLabel || currentLabel || scentValueLabel) {
      if (countLabel) countLabel.textContent = countText;
      if (currentLabel) currentLabel.textContent = currentText;
      if (scentValueLabel) scentValueLabel.textContent = scentLabel;

      var ariaLabel = countText ? countText + '. ' + currentText : currentText;
      note.setAttribute('aria-label', ariaLabel);
      return;
    }

    note.textContent = countText ? countText + ' . ' + currentText : currentText;
  }

  function applyRotatingScentVariant(card, variant) {
    if (!card || !variant) return;
    updateCardLinksForVariant(card, variant.url || '');
    updateCardImageForVariant(card, variant.image || '');
    updateScentLabel(card, variant.scent || '');
  }

  function initRotatingScentCards(scope) {
    var root = scope || document;
    var cards = [];

    if (root.nodeType === 1 && root.matches('[data-tall-owl-random-scent-card="true"]')) {
      cards.push(root);
    }
    if (root.querySelectorAll) {
      cards = cards.concat(
        Array.prototype.slice.call(root.querySelectorAll('[data-tall-owl-random-scent-card="true"]'))
      );
    }

    cards.forEach(function (card) {
      if (card.getAttribute('data-tall-owl-random-scent-ready') === 'true') return;

      var productId = (card.getAttribute('data-product-id') || '').trim();
      var dataNode = card.querySelector('script[data-tall-owl-random-scent-variants]');
      var variants = parseJsonData(dataNode ? dataNode.textContent : '');
      if (!Array.isArray(variants) || variants.length < 2) {
        card.setAttribute('data-tall-owl-random-scent-ready', 'true');
        return;
      }

      var selectedIndex = getRotatingScentIndex(productId || 'unknown', variants.length);
      applyRotatingScentVariant(card, variants[selectedIndex]);
      card.setAttribute('data-tall-owl-random-scent-ready', 'true');
    });
  }

  function showToast(message) {
    if (!message) return;
    var existing = document.querySelector('.tall-owl-toast');
    if (existing) existing.parentNode.removeChild(existing);
    var el = document.createElement('div');
    el.className = 'tall-owl-toast';
    el.setAttribute('role', 'status');
    el.textContent = message;
    document.body.appendChild(el);
    window.requestAnimationFrame(function () { el.classList.add('is-visible'); });
    window.setTimeout(function () {
      el.classList.remove('is-visible');
      window.setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 300);
    }, 4000);
  }
  function parsePositiveInt(value) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }
  function addToCart(variantId, quantity) {
    if (!variantId) return Promise.resolve(null);
    var qty = parsePositiveInt(quantity);
    if (!qty) qty = 1;

    // Route through the global cart-write queue so this add never collides with
    // an in-flight BOGO free-unit add or gift add/remove (Shopify drops one of
    // two concurrent /cart/* writes). Events dispatch after the write commits.
    return tallOwlQueueCartWrite(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: qty })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Failed to add');
          return response.json();
        })
        .then(function (product) {
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: { product: product, addToCartBtn: null }
            })
          );
          document.dispatchEvent(new CustomEvent('cart:build'));
          return product;
        });
    }).catch(function (error) {
      showToast('Unable to add this item right now.');
      throw error;
    });
  }
  function normalizeText(value) {
    return String(value || '').trim().toLowerCase();
  }
  // Cart-line variant swap: the per-line scent/size <select> in cart-item.liquid
  // swaps the line to the chosen variant in place (remove old line, add new one
  // at the same quantity) instead of forcing remove-and-re-add. Serialized
  // through the shared cart-write chain so it never races the gift/BOGO sync.
  function initTallOwlCartVariantSwap() {
    if (document.documentElement.hasAttribute('data-tall-owl-cart-variant-swap')) return;
    document.documentElement.setAttribute('data-tall-owl-cart-variant-swap', 'true');

    document.addEventListener('change', function (event) {
      var select = event.target.closest('[data-tall-owl-cart-variant-swap]');
      if (!select || select.disabled) return;
      var key = select.getAttribute('data-key');
      var qty = Number(select.getAttribute('data-qty')) || 1;
      var newId = Number(select.value);
      if (!key || !newId) return;

      select.disabled = true;
      var root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';

      tallOwlQueueCartWrite(function () {
        return fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Variant swap remove failed');
            return fetch(root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: newId, quantity: qty })
            });
          })
          .then(function (response) {
            if (!response.ok) throw new Error('Variant swap add failed');
            return response.json();
          });
      })
        .catch(function () {
          if (typeof showToast === 'function') showToast('Could not switch that one - please try again.');
        })
        .then(function () {
          // Rebuild either way: on success it shows the new variant, on failure
          // it restores what the cart really holds (the select may be stale).
          document.dispatchEvent(new CustomEvent('cart:build'));
        });
    });
  }

  function initCartRemoveLinkGuard() {
    if (document.documentElement.hasAttribute('data-tall-owl-cart-remove-guard')) return;
    document.documentElement.setAttribute('data-tall-owl-cart-remove-guard', 'true');

    document.addEventListener(
      'click',
      function (event) {
        var removeLink = event.target.closest('.cart__remove a[href*="/cart/change"]');
        if (!removeLink) return;

        var href = String(removeLink.getAttribute('href') || '');
        var cartItem = removeLink.closest('.cart__item');
        var form = removeLink.closest('form');
        var location = form ? String(form.getAttribute('data-location') || '').trim() : '';
        var qtyWrapper = cartItem ? cartItem.querySelector('.js-qty__wrapper') : null;
        var key = cartItem ? String(cartItem.getAttribute('data-key') || '').trim() : '';

        if (!key) {
          var hrefMatch = href.match(/[?&]id=([^&#]+)/i);
          key = hrefMatch && hrefMatch[1] ? decodeURIComponent(hrefMatch[1]) : '';
        }

        if (!key) return;

        event.preventDefault();

        if (removeLink.getAttribute('aria-disabled') === 'true') return;
        removeLink.setAttribute('aria-disabled', 'true');
        removeLink.classList.add('is-loading');

        if (qtyWrapper) {
          qtyWrapper.classList.add('is-loading');
        }

        if (location) {
          document.dispatchEvent(
            new CustomEvent('cart:quantity' + '.cart-' + location, {
              detail: [key, 0, qtyWrapper]
            })
          );
          return;
        }

        tallOwlQueueCartWrite(function () {
          return theme.cart.changeItem(key, 0);
        })
          .then(function (cart) {
            var parsedCart = JSON.parse(cart);
            document.dispatchEvent(
              new CustomEvent('cart:updated', {
                detail: {
                  cart: parsedCart
                }
              })
            );
            document.dispatchEvent(new CustomEvent('cart:build'));
          })
          .catch(function () {
            removeLink.removeAttribute('aria-disabled');
            removeLink.classList.remove('is-loading');
            if (qtyWrapper) {
              qtyWrapper.classList.remove('is-loading');
            }
            showToast('Unable to update your cart right now.');
          });
      },
      true
    );
  }
  function initTallOwlCartCrossSellAdd() {
    if (document.documentElement.hasAttribute('data-tall-owl-cart-xs-bound')) return;
    document.documentElement.setAttribute('data-tall-owl-cart-xs-bound', 'true');

    function xsFormatMoney(cents) {
      if (window.Shopify && Shopify.formatMoney && window.theme && theme.settings) {
        return Shopify.formatMoney(cents, theme.settings.moneyFormat);
      }
      return '$' + (Number(cents || 0) / 100).toFixed(2);
    }

    function xsEsc(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    // Keep the row's price + the add button's variant in sync when the shopper
    // picks a scent/size. Delegated so it survives every cart-drawer re-render.
    // Pairable rows (cart has an unpaired BOGO unit) re-render the pair math -
    // same rule as the Liquid: min(variant, unpaired) is the unit that halves.
    document.addEventListener('change', function (event) {
      var select = event.target.closest('[data-tall-owl-cart-xs-scent]');
      if (!select) return;
      var item = select.closest('.tall-owl-cart-xs__item');
      if (!item) return;
      var opt = select.options[select.selectedIndex];
      if (!opt) return;
      var button = item.querySelector('[data-tall-owl-cart-xs-add]');
      if (button && opt.value) button.setAttribute('data-variant-id', opt.value);

      var priceBox = item.hasAttribute('data-tall-owl-xs-pairable')
        ? item.querySelector('[data-tall-owl-cart-xs-pricebox]')
        : null;
      var cents = Number(opt.getAttribute('data-cents') || 0);
      var unpairedCents = Number(item.getAttribute('data-unpaired-cents') || 0);
      if (priceBox && cents > 0 && unpairedCents > 0) {
        priceBox.innerHTML =
          '<span data-tall-owl-cart-xs-price>' + xsEsc(xsFormatMoney(cents)) + '</span> ' +
          '<span class="tall-owl-cart-xs__pairnote">completes a BOGO pair &middot; discount confirmed after add</span>';
        return;
      }

      var priceEl = item.querySelector('[data-tall-owl-cart-xs-price]');
      var priceText = opt.getAttribute('data-price');
      if (priceEl && priceText) priceEl.textContent = priceText;
    });

    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-tall-owl-cart-xs-add]');
      if (!button || button.disabled) return;
      event.preventDefault();

      // Resolve the variant at click time, preferring the scent picker's current
      // value over the button default (guards against a change event that hasn't
      // fired yet, e.g. a fast tap right after picking on mobile).
      var item = button.closest('.tall-owl-cart-xs__item');
      var scentSelect = item ? item.querySelector('[data-tall-owl-cart-xs-scent]') : null;
      var variantId = scentSelect && scentSelect.value ? scentSelect.value : button.getAttribute('data-variant-id');
      if (!variantId) return;

      var label = button.querySelector('.tall-owl-cart-xs__add-label');
      var originalLabelText = label ? label.textContent : '';

      button.disabled = true;
      button.classList.add('is-loading');
      if (scentSelect) scentSelect.disabled = true;
      if (label) label.textContent = 'Adding...';

      var root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      tallOwlQueueCartWrite(function () {
        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Add failed');
            return response.json();
          })
          .then(function (product) {
            if (label) label.textContent = 'Added';
            // ajaxProduct:added keeps the theme's add-to-cart plumbing (drawer
            // refresh, gift sync) in the loop; the drawer is already open here.
            document.dispatchEvent(
              new CustomEvent('ajaxProduct:added', {
                detail: { product: product, addToCartBtn: null }
              })
            );
            document.dispatchEvent(new CustomEvent('cart:build'));
          });
      })
        .catch(function () {
          button.disabled = false;
          button.classList.remove('is-loading');
          if (scentSelect) scentSelect.disabled = false;
          if (label) label.textContent = originalLabelText || '+ Add';
        });
    });
  }

  function initTallOwlPdpPairModules(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var modules = scope.querySelectorAll('[data-tall-owl-pair-module]');
    var homepageSections = Array.prototype.slice.call(
      scope.querySelectorAll('[data-tall-owl-pick-pair]')
    );
    if (scope.matches && scope.matches('[data-tall-owl-pick-pair]')) {
      homepageSections.unshift(scope);
    }

    function formatPairMoney(cents) {
      if (window.Shopify && Shopify.formatMoney && window.theme && theme.settings) {
        return Shopify.formatMoney(cents, theme.settings.moneyFormat);
      }
      return '$' + (Number(cents || 0) / 100).toFixed(2);
    }

    function ensureCommerceAnalytics() {
      if (typeof window.tallOwlEnsureCommerceAnalytics !== 'function') {
        return Promise.resolve();
      }
      try {
        return Promise.resolve(window.tallOwlEnsureCommerceAnalytics()).catch(function () {
          // Provider readiness must never block the pair request.
        });
      } catch (error) {
        return Promise.resolve();
      }
    }

    function fetchPairCart() {
      var route = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      return fetch(route + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        if (!response.ok) throw new Error('Unable to read cart');
        return response.json();
      });
    }

    function quantityForVariant(cart, variantId) {
      return (cart.items || []).reduce(function (total, item) {
        return String(item.variant_id) === String(variantId) ? total + Number(item.quantity || 0) : total;
      }, 0);
    }

    function rollbackPairItems(addedItems, beforeByKey) {
      var route = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      return (addedItems || []).reduce(function (chain, item) {
        return chain.then(function () {
          if (!item || !item.key) return null;
          return fetch(route + 'cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: Number(beforeByKey[item.key] || 0)
            })
          }).then(function (response) {
            if (!response.ok) throw new Error('Unable to restore cart');
            return response.json();
          });
        });
      }, Promise.resolve());
    }

    function updatePairModule(module, variant) {
      if (!module || !variant) return;

      var variantId = String(variant.id || '');
      var price = Number(variant.price || 0);
      var available = variant.available !== false;
      module.setAttribute('data-selected-variant-id', variantId);
      module.setAttribute('data-selected-variant-price', String(price));
      module.setAttribute('data-selected-variant-available', available ? 'true' : 'false');

      var variantLabel = module.querySelector('[data-tall-owl-pair-variant-label]');
      if (variantLabel) variantLabel.textContent = variant.public_title || variant.title || '';

      var sameOffer = module.querySelector('[data-tall-owl-pair-offer="same"]');
      if (sameOffer) {
        var sameTotal = Math.floor((price * 3) / 2);
        var sameSavings = Math.floor(price / 2);
        var sameCopy = sameOffer.querySelector('[data-tall-owl-pair-copy]');
        var sameButton = sameOffer.querySelector('[data-tall-owl-pair-add]');
        if (sameCopy) {
          sameCopy.textContent =
            '2 for ' +
            formatPairMoney(sameTotal) +
            ' — save ' +
            formatPairMoney(sameSavings) +
            (sameTotal >= 5000 ? ' + free shipping + free Lip Balm' : '');
        }
        if (sameButton) {
          sameButton.setAttribute('data-pair-total-cents', String(sameTotal));
          sameButton.setAttribute('data-pair-savings-cents', String(sameSavings));
          sameButton.disabled = !available;
        }
      }

      var freedomOffer = module.querySelector('[data-tall-owl-pair-offer="freedom"]');
      if (freedomOffer) {
        var freedomPrice = Number(module.getAttribute('data-freedom-variant-price') || 0);
        var mixedSavings = Math.floor(Math.min(price, freedomPrice) / 2);
        var mixedTotal = price + freedomPrice - mixedSavings;
        var mixedCopy = freedomOffer.querySelector('[data-tall-owl-pair-copy]');
        var mixedButton = freedomOffer.querySelector('[data-tall-owl-pair-add]');
        if (mixedCopy) {
          mixedCopy.textContent =
            formatPairMoney(mixedTotal) +
            ' — save ' +
            formatPairMoney(mixedSavings) +
            ' + free shipping + free Lip Balm';
        }
        if (mixedButton) {
          mixedButton.setAttribute('data-pair-total-cents', String(mixedTotal));
          mixedButton.setAttribute('data-pair-savings-cents', String(mixedSavings));
          mixedButton.disabled = !available;
        }
      }
    }

    function bindPairModule(module) {
      if (!module || module.getAttribute('data-tall-owl-pair-bound') === 'true') return;
      module.setAttribute('data-tall-owl-pair-bound', 'true');

      var section = module.closest('.product-section');
      var variantsNode = module.querySelector('[data-tall-owl-pair-variants]');
      var variants = [];
      try {
        variants = JSON.parse(variantsNode ? variantsNode.textContent : '[]');
      } catch (error) {
        variants = [];
      }

      if (section) {
        section.addEventListener('variantChange', function (event) {
          var variant = event.detail && event.detail.variant;
          if (variant) updatePairModule(module, variant);
        });
      }

      module.addEventListener('click', function (event) {
        var button = event.target.closest('[data-tall-owl-pair-add]');
        if (!button || button.disabled) return;
        event.preventDefault();

        var selectedId = String(module.getAttribute('data-selected-variant-id') || '');
        var pairType = String(button.getAttribute('data-pair-type') || '');
        var freedomId = String(module.getAttribute('data-freedom-variant-id') || '');
        var selectedVariant = variants.filter(function (variant) {
          return String(variant.id) === selectedId;
        })[0];
        var status = module.querySelector('[data-tall-owl-pair-status]');
        var buttons = module.querySelectorAll('[data-tall-owl-pair-add]');
        var requestItems = [];
        var expectedDeltas = {};

        if (!selectedId || !selectedVariant || selectedVariant.available === false) {
          if (status) status.textContent = 'Choose an available scent and size first.';
          return;
        }

        if (pairType === 'same_variant') {
          requestItems.push({ id: Number(selectedId), quantity: 2 });
          expectedDeltas[selectedId] = 2;
        } else if (pairType === 'freedom_balm' && freedomId) {
          requestItems.push({ id: Number(selectedId), quantity: 1 });
          requestItems.push({ id: Number(freedomId), quantity: 1 });
          expectedDeltas[selectedId] = (expectedDeltas[selectedId] || 0) + 1;
          expectedDeltas[freedomId] = (expectedDeltas[freedomId] || 0) + 1;
        } else {
          if (status) status.textContent = 'This pairing is not available right now.';
          return;
        }

        document.dispatchEvent(
          new CustomEvent('tallowl:pair-click', {
            detail: {
              pair_type: pairType,
              product_id: String(module.getAttribute('data-product-id') || ''),
              product_handle: String(module.getAttribute('data-product-handle') || ''),
              variant_ids: Object.keys(expectedDeltas),
              pair_total_cents: Number(button.getAttribute('data-pair-total-cents') || 0),
              pair_savings_cents: Number(button.getAttribute('data-pair-savings-cents') || 0)
            }
          })
        );

        buttons.forEach(function (pairButton) {
          pairButton.disabled = true;
          pairButton.classList.add('is-loading');
          pairButton.setAttribute('aria-busy', 'true');
        });
        if (status) {
          status.classList.remove('is-error', 'is-success');
          status.textContent = 'Adding both items…';
        }

        var beforeCart;
        var beforeByKey = {};
        var addedItems = [];
        var route = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';

        tallOwlQueueCartWrite(function () {
          return ensureCommerceAnalytics()
            .then(function () {
              return fetchPairCart();
            })
            .then(function (cart) {
              beforeCart = cart;
              (cart.items || []).forEach(function (item) {
                beforeByKey[item.key] = Number(item.quantity || 0);
              });
              return fetch(route + 'cart/add.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: requestItems })
              });
            })
            .then(function (response) {
              if (!response.ok) throw new Error('Pair add failed');
              return response.json();
            })
            .then(function (responsePayload) {
              addedItems = responsePayload && responsePayload.items ? responsePayload.items : [];
              return fetchPairCart();
            })
            .then(function (afterCart) {
              var confirmed = Object.keys(expectedDeltas).every(function (variantId) {
                return (
                  quantityForVariant(afterCart, variantId) - quantityForVariant(beforeCart, variantId) ===
                  expectedDeltas[variantId]
                );
              });

              if (!confirmed) {
                return rollbackPairItems(addedItems, beforeByKey)
                  .catch(function () {
                    /* The error below asks the shopper to review the cart. */
                  })
                  .then(function () {
                    throw new Error('Pair quantities were not confirmed');
                  });
              }

              document.dispatchEvent(
                new CustomEvent('tallowl:pair-added', {
                  detail: {
                    pair_type: pairType,
                    product_id: String(module.getAttribute('data-product-id') || ''),
                    product_handle: String(module.getAttribute('data-product-handle') || ''),
                    variant_ids: Object.keys(expectedDeltas),
                    pair_total_cents: Number(button.getAttribute('data-pair-total-cents') || 0),
                    pair_savings_cents: Number(button.getAttribute('data-pair-savings-cents') || 0),
                    cart_total_cents: Number(afterCart.total_price || 0)
                  }
                })
              );
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: afterCart } }));
              document.dispatchEvent(
                new CustomEvent('ajaxProduct:added', {
                  detail: { product: addedItems[0] || null, addToCartBtn: null }
                })
              );
              document.dispatchEvent(new CustomEvent('cart:build'));
              if (status) {
                status.classList.add('is-success');
                status.textContent = 'Pair added. Your cart confirms both items.';
              }
            });
        })
          .catch(function () {
            if (status) {
              status.classList.add('is-error');
              status.textContent = 'We could not confirm both items. Please try again and review your cart.';
            }
          })
          .then(function () {
            buttons.forEach(function (pairButton) {
              pairButton.classList.remove('is-loading');
              pairButton.removeAttribute('aria-busy');
              pairButton.disabled = module.getAttribute('data-selected-variant-available') !== 'true';
            });
          });
      });
    }

    function bindHomepagePairSection(section) {
      if (!section || section.getAttribute('data-pp-bound') === 'true') return;
      section.setAttribute('data-pp-bound', 'true');

      section.addEventListener('click', function (event) {
        var button = event.target.closest('[data-pp-add]');
        if (!button || button.disabled) return;
        event.preventDefault();

        var baseVariantId = String(button.getAttribute('data-base-variant') || '');
        var addonVariantId = String(button.getAttribute('data-addon-variant') || '');
        var baseVariant = Number(baseVariantId);
        var addonVariant = Number(addonVariantId);
        var card = button.closest('.tall-owl-pair-band__card');
        var status = card ? card.querySelector('[data-pp-status]') : null;
        var buttons = section.querySelectorAll('[data-pp-add]');
        var label = button.querySelector('.tall-owl-pair-band__add-label');
        var originalLabel = label ? label.textContent : 'Add both';
        var expectedDeltas = {};
        var requestItems = [];

        if (!baseVariant || !addonVariant) {
          if (status) {
            status.classList.add('is-error');
            status.textContent = 'This pair is unavailable right now. Please choose products individually.';
          }
          return;
        }

        expectedDeltas[baseVariantId] = (expectedDeltas[baseVariantId] || 0) + 1;
        expectedDeltas[addonVariantId] = (expectedDeltas[addonVariantId] || 0) + 1;
        if (baseVariantId === addonVariantId) {
          requestItems.push({ id: baseVariant, quantity: 2 });
        } else {
          requestItems.push({ id: baseVariant, quantity: 1 });
          requestItems.push({ id: addonVariant, quantity: 1 });
        }

        document.dispatchEvent(
          new CustomEvent('tallowl:pair-click', {
            detail: {
              pair_type: String(button.getAttribute('data-pair-type') || 'mixed_full_size'),
              placement: 'homepage_pair_band',
              product_id: String(button.getAttribute('data-product-id') || ''),
              product_handle: String(button.getAttribute('data-product-handle') || ''),
              variant_ids: Object.keys(expectedDeltas),
              pair_total_cents: Number(button.getAttribute('data-pair-total-cents') || 0),
              pair_savings_cents: Number(button.getAttribute('data-pair-savings-cents') || 0)
            }
          })
        );

        buttons.forEach(function (pairButton) {
          pairButton.disabled = true;
          pairButton.classList.add('is-loading');
          pairButton.setAttribute('aria-busy', 'true');
        });
        if (label) label.textContent = 'Adding…';
        if (status) {
          status.classList.remove('is-error', 'is-success');
          status.textContent = 'Adding and confirming both items…';
        }

        var beforeCart;
        var beforeByKey = {};
        var addedItems = [];
        var route = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';

        tallOwlQueueCartWrite(function () {
          return fetchPairCart()
            .then(function (cart) {
              beforeCart = cart;
              (cart.items || []).forEach(function (item) {
                beforeByKey[item.key] = Number(item.quantity || 0);
              });
              return fetch(route + 'cart/add.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: requestItems })
              });
            })
            .then(function (response) {
              if (!response.ok) throw new Error('Pair add failed');
              return response.json();
            })
            .then(function (responsePayload) {
              addedItems = responsePayload && responsePayload.items ? responsePayload.items : [];
              return fetchPairCart();
            })
            .then(function (afterCart) {
              var confirmed = Object.keys(expectedDeltas).every(function (variantId) {
                return (
                  quantityForVariant(afterCart, variantId) - quantityForVariant(beforeCart, variantId) ===
                  expectedDeltas[variantId]
                );
              });

              if (!confirmed) {
                return rollbackPairItems(addedItems, beforeByKey)
                  .catch(function () {
                    /* The message below asks the shopper to review the cart. */
                  })
                  .then(function () {
                    throw new Error('Pair quantities were not confirmed');
                  });
              }

              document.dispatchEvent(
                new CustomEvent('tallowl:pair-added', {
                  detail: {
                    pair_type: String(button.getAttribute('data-pair-type') || 'mixed_full_size'),
                    placement: 'homepage_pair_band',
                    product_id: String(button.getAttribute('data-product-id') || ''),
                    product_handle: String(button.getAttribute('data-product-handle') || ''),
                    variant_ids: Object.keys(expectedDeltas),
                    pair_total_cents: Number(button.getAttribute('data-pair-total-cents') || 0),
                    pair_savings_cents: Number(button.getAttribute('data-pair-savings-cents') || 0),
                    cart_total_cents: Number(afterCart.total_price || 0)
                  }
                })
              );
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: afterCart } }));
              document.dispatchEvent(
                new CustomEvent('ajaxProduct:added', {
                  detail: { product: addedItems[0] || null, addToCartBtn: null }
                })
              );
              document.dispatchEvent(new CustomEvent('cart:build'));
              if (label) label.textContent = 'Added';
              if (status) {
                status.classList.add('is-success');
                status.textContent = 'Pair added. Your cart confirms both items.';
              }
            });
        })
          .catch(function () {
            if (label) label.textContent = originalLabel || 'Add both';
            if (status) {
              status.classList.add('is-error');
              status.textContent = 'We could not confirm both items. Please review your cart and try again.';
            }
          })
          .then(function () {
            if (label) label.textContent = originalLabel || 'Add both';
            buttons.forEach(function (pairButton) {
              pairButton.classList.remove('is-loading');
              pairButton.removeAttribute('aria-busy');
              pairButton.disabled = false;
            });
          });
      });
    }

    modules.forEach(bindPairModule);
    homepageSections.forEach(bindHomepagePairSection);
  }

  function normalizeTallOwlFullCart() {
    if (!document.body || !document.body.classList.contains('template-cart')) return;
    var form = document.getElementById('CartPageForm');
    if (!form) return;
    var products = form.querySelector('[data-products]');
    if (!products) return;

    var giftDetails = [];
    products.querySelectorAll('.cart__item').forEach(function (item) {
      if (!item.querySelector('.tall-owl-gift-line')) return;
      var details = item.closest('.tall-owl-cart-gift-row');
      if (!details) {
        details = document.createElement('details');
        details.className = 'tall-owl-cart-gift-row';
        details.setAttribute('data-tall-owl-cart-gift-row', '');

        var summary = document.createElement('summary');
        summary.className = 'tall-owl-cart-gift-row__summary';
        var label = document.createElement('span');
        label.textContent = 'Threshold gift · Lip Balm';
        var badge = document.createElement('span');
        badge.className = 'tall-owl-cart-gift-row__badge';
        badge.textContent = 'Free';
        summary.append(label, badge);

        item.parentNode.insertBefore(details, item);
        details.append(summary, item);
      }
      giftDetails.push(details);
    });

    giftDetails.forEach(function (details) {
      details.parentNode.appendChild(details);
    });

    var ajaxItems = products.querySelector('.cart__items[data-cart-subtotal]');
    var subtotalCents = ajaxItems ? Number(ajaxItems.getAttribute('data-cart-subtotal') || 0) : 0;
    var subtotal = form.querySelector('[data-tall-owl-mobile-cart-subtotal]');
    if (subtotal && ajaxItems) {
      if (window.Shopify && Shopify.formatMoney && window.theme && theme.settings) {
        subtotal.textContent = Shopify.formatMoney(subtotalCents, theme.settings.moneyFormat);
      } else {
        subtotal.textContent = '$' + (subtotalCents / 100).toFixed(2);
      }
    }

    var bogoSummary = form.querySelector('[data-tall-owl-bogo-summary]');
    var savingsCents = Number(bogoSummary ? bogoSummary.getAttribute('data-bogo-savings-cents') : 0);
    var savings = form.querySelector('[data-tall-owl-mobile-cart-savings]');
    if (savings) {
      if (savingsCents > 0) {
        var formattedSavings =
          window.Shopify && Shopify.formatMoney && window.theme && theme.settings
            ? Shopify.formatMoney(savingsCents, theme.settings.moneyFormat)
            : '$' + (savingsCents / 100).toFixed(2);
        savings.textContent = 'BOGO 50 saved ' + formattedSavings;
        savings.hidden = false;
      } else {
        savings.textContent = '';
        savings.hidden = true;
      }
    }
  }

  function initCartDrawerScrollReset() {
    document.addEventListener('drawerOpen.CartDrawer', function () {
      var resetScroll = function () {
        var drawer = document.getElementById('CartDrawer');
        if (!drawer) return;

        var inner = drawer.querySelector('.drawer__inner');
        if (inner) {
          inner.scrollTop = 0;
        }

        var scrollable = drawer.querySelector('.drawer__inner > .drawer__scrollable');
        if (scrollable) {
          scrollable.scrollTop = 0;
        }
      };

      window.requestAnimationFrame(resetScroll);
      window.setTimeout(resetScroll, 120);
      window.setTimeout(resetScroll, 360);
    });
  }

  function initTallOwlBogo50PopupOffer() {
    var selector = '[data-tall-owl-bogo-50-popup-offer]';
    var renderToken = 0;
    var dismissPrefix = 'tallOwlBogo50PopupDismissed:';

    function getMount() {
      return document.querySelector(selector);
    }

    function isEnabled(mount) {
      return (
        mount &&
        mount.getAttribute('data-enabled') === 'true' &&
        mount.getAttribute('data-bogo-enabled') === 'true'
      );
    }

    function esc(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    function normalize(value) {
      return String(value || '').trim().toLowerCase();
    }

    function formatMoney(cents) {
      if (window.Shopify && Shopify.formatMoney && window.theme && theme.settings) {
        return Shopify.formatMoney(cents, theme.settings.moneyFormat);
      }
      return '$' + (Number(cents || 0) / 100).toFixed(2);
    }

    function getBogoSavings(cart, titleToken) {
      var titleNeedle = normalize(titleToken || 'BOGO 50');
      var savings = 0;
      var items = cart && Array.isArray(cart.items) ? cart.items : [];

      items.forEach(function (item) {
        var allocations = item.line_level_discount_allocations || [];
        allocations.forEach(function (allocation) {
          var app = allocation.discount_application || {};
          var title = normalize(app.title || allocation.title || '');
          var value = Number(app.value || 0);
          var valueType = normalize(app.value_type || '');
          if (title.indexOf(titleNeedle) !== -1) {
            savings += Number(allocation.amount || 0);
          } else if (title.indexOf('bogos-') !== -1 && value === 50 && valueType === 'percentage') {
            savings += Number(allocation.amount || 0);
          }
        });
      });

      return savings;
    }

    function fetchCart() {
      var root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      return fetch(root + 'cart.js', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      });
    }

    function sizedImageUrl(src, width) {
      if (!src) return '';
      var stringified = typeof src === 'string' ? src : (src.src || src.url || src.original_src || '');
      if (!stringified) return '';
      if (stringified.indexOf('//') === 0) stringified = window.location.protocol + stringified;
      var w = width || 144;
      return stringified + (stringified.indexOf('?') === -1 ? '?width=' + w : '&width=' + w);
    }

    function getAddedItemImage(item) {
      if (!item) return '';
      var src = item.image || item.featured_image || '';
      return sizedImageUrl(src, 144);
    }

    function isDismissedState(state) {
      return readStorageValue(window.sessionStorage, dismissPrefix + state) === '1';
    }

    function getShippingProgressHtml(cart) {
      var thresholdCents = 5000;
      var subtotalCents = Number((cart && cart.total_price) || 0);
      var remainingCents = thresholdCents - subtotalCents;
      if (remainingCents <= 0) {
        return '<p class="tall-owl-bogo-50-popup-offer__ship tall-owl-bogo-50-popup-offer__ship--unlocked"><span aria-hidden="true">&#10003;</span> Free shipping unlocked</p>';
      }
      var label = formatMoney(remainingCents);
      return '<p class="tall-owl-bogo-50-popup-offer__ship">You\'re <strong>' + esc(label) + '</strong> from free shipping</p>';
    }

    function markStateDismissed(state) {
      if (!state) return;
      writeStorageValue(window.sessionStorage, dismissPrefix + state, '1');
    }

    function hideMount(mount) {
      if (!mount) return;
      mount.innerHTML = '';
      mount.hidden = true;
      mount.setAttribute('aria-hidden', 'true');
      mount.removeAttribute('data-popup-state');
      document.documentElement.classList.remove('tall-owl-bogo-50-popup-open');
    }

    function focusDialog(mount) {
      window.setTimeout(function () {
        var dialog = mount.querySelector('.tall-owl-bogo-50-popup-offer__dialog');
        if (dialog) dialog.focus({ preventScroll: true });
      }, 30);
    }

    var productCache = {};
    var recentAutoAdds = {}; // variantId -> timestamp; one add can fire two events
    var swapChain = Promise.resolve(); // serializes scent swaps; UI stays optimistic

    // Quiet add - deliberately does NOT dispatch ajaxProduct:added, so the free
    // one never re-triggers this popup's own auto-add flow.
    function quietAdd(variantId) {
      return tallOwlQueueCartWrite(function () {
        var root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
        return fetch(root + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        }).then(function (response) {
          if (!response.ok) throw new Error('Add failed');
          return response.json();
        });
      });
    }

    function cartUpdateQuantities(updates) {
      return tallOwlQueueCartWrite(function () {
        var root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (response) {
          if (!response.ok) throw new Error('Cart update failed');
          return response.json();
        });
      });
    }

    // Refresh the drawer the same way the gift flow does (no drawer auto-open).
    function notifyCartChanged() {
      return fetchCart()
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent('cart:build'));
          return cart;
        })
        .catch(function () {
          document.dispatchEvent(new CustomEvent('cart:build'));
          return null;
        });
    }

    function fetchProductByHandle(handle) {
      if (!handle) return Promise.resolve(null);
      if (productCache[handle]) return productCache[handle];
      var root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      productCache[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; });
      return productCache[handle];
    }

    function getScentOptionIndex(product) {
      var options = (product && product.options) || [];
      for (var i = 0; i < options.length; i++) {
        var name = typeof options[i] === 'string' ? options[i] : (options[i] && options[i].name) || '';
        if (/scent/i.test(name)) return i;
      }
      return -1;
    }

    function getVariantOptions(variant) {
      if (variant && Array.isArray(variant.options)) return variant.options;
      return [variant && variant.option1, variant && variant.option2, variant && variant.option3];
    }

    // Variants that differ from the base variant ONLY by scent (same size etc.).
    function getScentSiblings(product, baseVariant) {
      var scentIdx = getScentOptionIndex(product);
      if (scentIdx === -1 || !baseVariant) return [];
      var baseOptions = getVariantOptions(baseVariant);
      var siblings = (product.variants || []).filter(function (variant) {
        if (!variant.available) return false;
        var options = getVariantOptions(variant);
        for (var i = 0; i < baseOptions.length; i++) {
          if (i === scentIdx) continue;
          if ((options[i] || null) !== (baseOptions[i] || null)) return false;
        }
        return true;
      });
      return siblings.length > 1 ? siblings : [];
    }

    function cleanProductTitle(title) {
      return String(title || '').replace(/^\*NEW\*\s*/i, '');
    }

    // Renders instantly with the data we already have from the add event; the
    // scent chips and shipping bar fill in as their requests resolve.
    function renderFreeAddedState(mount, addedItem, baseVariantId, cart) {
      var shippingHtml = cart ? getShippingProgressHtml(cart) : '';
      var addedTitle = cleanProductTitle(
        (addedItem && (addedItem.product_title || addedItem.title)) || 'this item'
      );
      var addedImage = getAddedItemImage(addedItem);
      var heroClass = 'tall-owl-bogo-50-popup-offer__hero' + (addedImage ? ' has-image' : '');
      var imageHtml = addedImage
        ? '<img class="tall-owl-bogo-50-popup-offer__image" src="' + esc(addedImage) + '" alt="' + esc(addedTitle) + '" loading="eager" width="72" height="72">'
        : '';

      mount.innerHTML =
        '<div class="tall-owl-bogo-50-popup-offer__backdrop" data-bogo-50-dismiss></div>' +
        '<section class="tall-owl-bogo-50-popup-offer__dialog" role="dialog" aria-modal="true" aria-label="Buy One, Get One 50% Off" tabindex="-1">' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__close" data-bogo-50-dismiss aria-label="Close Buy One, Get One 50% Off message">&times;</button>' +
        '<div class="' + heroClass + '">' +
        imageHtml +
        '<div class="tall-owl-bogo-50-popup-offer__hero-copy">' +
        '<span class="tall-owl-bogo-50-popup-offer__badge">Buy One, Get One 50% Off</span>' +
        '<p class="tall-owl-bogo-50-popup-offer__title">Your second one is in the cart</p>' +
        '<p class="tall-owl-bogo-50-popup-offer__text" data-bogo-50-subtext>We added a second ' + esc(addedTitle) + ' to your cart at 50% off - remove it anytime.</p>' +
        '</div>' +
        '</div>' +
        '<div data-bogo-50-chips></div>' +
        '<div data-bogo-50-ship>' + shippingHtml + '</div>' +
        '<div class="tall-owl-bogo-50-popup-offer__actions">' +
        '<button type="button" class="btn tall-owl-bogo-50-popup-offer__add" data-bogo-50-view-cart>View cart</button>' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__dismiss" data-bogo-50-dismiss>Done</button>' +
        '</div>' +
        '<p class="tall-owl-bogo-50-popup-offer__note">Buy one, get one 50% off - mix and match any two eligible full-size products. Lip Balm excluded. No code needed.</p>' +
        '</section>';

      mount.setAttribute('data-popup-state', 'free-added');
      mount.setAttribute('data-bogo-50-free-variant', String(baseVariantId));
      mount.setAttribute('data-bogo-50-desired-variant', String(baseVariantId));
      mount.hidden = false;
      mount.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('tall-owl-bogo-50-popup-open');
      document.dispatchEvent(new CustomEvent('cart:close'));
      focusDialog(mount);
    }

    // TWO-STEP opt-in offer, so no single screen is cramped. Step 1
    // (renderOfferChoice) asks same-product vs a different product; step 2 shows
    // ONLY the scents (renderScentStep) or ONLY the product picker
    // (renderPairStep). The offer is held in `currentOffer` so Back re-renders
    // without a re-fetch. Nothing touches the cart until an explicit "Add".
    var currentOffer = null;

    function offerHead(ariaLabel) {
      return '<div class="tall-owl-bogo-50-popup-offer__backdrop" data-bogo-50-dismiss></div>' +
        '<section class="tall-owl-bogo-50-popup-offer__dialog" role="dialog" aria-modal="true" aria-label="' + esc(ariaLabel) + '" tabindex="-1">' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__close" data-bogo-50-dismiss aria-label="Close Buy One, Get One 50% Off offer">&times;</button>';
    }

    function openOffer(mount, state) {
      mount.setAttribute('data-popup-state', state);
      mount.hidden = false;
      mount.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('tall-owl-bogo-50-popup-open');
      document.dispatchEvent(new CustomEvent('cart:close'));
      focusDialog(mount);
    }

    // Step 1: pick a path.
    function renderOfferChoice(mount) {
      var offer = currentOffer || {};
      var addedTitle = cleanProductTitle(offer.title || 'this item');
      var addedImage = offer.image;
      var heroClass = 'tall-owl-bogo-50-popup-offer__hero' + (addedImage ? ' has-image' : '');
      var imageHtml = addedImage
        ? '<img class="tall-owl-bogo-50-popup-offer__image" src="' + esc(addedImage) + '" alt="' + esc(addedTitle) + '" loading="eager" width="72" height="72">'
        : '';
      mount.innerHTML =
        offerHead('Buy One, Get One 50% Off') +
        '<div class="' + heroClass + '">' + imageHtml +
        '<div class="tall-owl-bogo-50-popup-offer__hero-copy">' +
        '<span class="tall-owl-bogo-50-popup-offer__badge">Buy One, Get One 50% Off</span>' +
        '<p class="tall-owl-bogo-50-popup-offer__title">Want a second at 50% off?</p>' +
        '<p class="tall-owl-bogo-50-popup-offer__text">Mix and match any two eligible full-size products - the lower-priced one is 50% off. Lip Balm excluded.</p>' +
        '</div></div>' +
        '<div class="tall-owl-bogo-50-popup-offer__paths">' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__path" data-bogo-50-choose-same>' +
        '<span class="tall-owl-bogo-50-popup-offer__path-label">Another ' + esc(addedTitle) + '</span>' +
        '<span class="tall-owl-bogo-50-popup-offer__path-sub">Same product - 50% off</span></button>' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__path" data-bogo-50-choose-different>' +
        '<span class="tall-owl-bogo-50-popup-offer__path-label">Pair a different product</span>' +
        '<span class="tall-owl-bogo-50-popup-offer__path-sub">Mix and match - lower-priced is 50% off</span></button>' +
        '</div>' +
        '<div data-bogo-50-ship>' + (offer.cart ? getShippingProgressHtml(offer.cart) : '') + '</div>' +
        '<div class="tall-owl-bogo-50-popup-offer__actions">' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__dismiss" data-bogo-50-dismiss>No thanks</button>' +
        '</div>' +
        '</section>';
      openOffer(mount, 'offer');
    }

    // Step 2a: scents for the same product + the Add button.
    function renderScentStep(mount) {
      var offer = currentOffer || {};
      var addedTitle = cleanProductTitle(offer.title || 'this item');
      mount.innerHTML =
        offerHead('Add another ' + addedTitle) +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__back" data-bogo-50-back>&larr; Back</button>' +
        '<div class="tall-owl-bogo-50-popup-offer__hero-copy">' +
        '<span class="tall-owl-bogo-50-popup-offer__badge">Buy One, Get One 50% Off</span>' +
        '<p class="tall-owl-bogo-50-popup-offer__title">Add another ' + esc(addedTitle) + '</p>' +
        '<p class="tall-owl-bogo-50-popup-offer__text" data-bogo-50-subtext>Your second one is 50% off - tap Add below.</p>' +
        '</div>' +
        '<div data-bogo-50-chips></div>' +
        '<div data-bogo-50-ship>' + (offer.cart ? getShippingProgressHtml(offer.cart) : '') + '</div>' +
        '<div class="tall-owl-bogo-50-popup-offer__actions">' +
        '<button type="button" class="btn tall-owl-bogo-50-popup-offer__add" data-bogo-50-add-second>Add at 50% off</button>' +
        '</div>' +
        '</section>';
      mount.setAttribute('data-bogo-50-free-variant', String(offer.baseVariantId));
      mount.setAttribute('data-bogo-50-desired-variant', String(offer.baseVariantId));
      openOffer(mount, 'offer-scent');
      if (offer.handle) {
        fetchProductByHandle(offer.handle).then(function (product) {
          fillScentChips(mount, product, offer.baseVariantId);
        });
      }
    }

    // Step 2b: pick a different product to pair.
    function renderPairStep(mount) {
      var offer = currentOffer || {};
      mount.innerHTML =
        offerHead('Pair a different product at 50% off') +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__back" data-bogo-50-back>&larr; Back</button>' +
        '<div class="tall-owl-bogo-50-popup-offer__hero-copy">' +
        '<span class="tall-owl-bogo-50-popup-offer__badge">Buy One, Get One 50% Off</span>' +
        '<p class="tall-owl-bogo-50-popup-offer__title">Pair a different product</p>' +
        '<p class="tall-owl-bogo-50-popup-offer__text">Add any product - your lower-priced item is 50% off.</p>' +
        '</div>' +
        '<div data-bogo-50-pair></div>' +
        '<div data-bogo-50-ship>' + (offer.cart ? getShippingProgressHtml(offer.cart) : '') + '</div>' +
        '<div class="tall-owl-bogo-50-popup-offer__actions">' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__dismiss" data-bogo-50-dismiss>No thanks</button>' +
        '</div>' +
        '</section>';
      openOffer(mount, 'offer-pair');
      fillPairSuggestions(mount, offer.handle);
    }

    // Fill the scent chips once the product JSON is in (usually a few ms,
    // and cached after the first add of a product).
    function fillScentChips(mount, product, baseVariantId) {
      var slot = mount.querySelector('[data-bogo-50-chips]');
      if (!slot || !product) return;

      var baseVariant = (product.variants || []).filter(function (variant) {
        return Number(variant.id) === Number(baseVariantId);
      })[0] || null;
      var scentIdx = getScentOptionIndex(product);
      var siblings = baseVariant ? getScentSiblings(product, baseVariant) : [];
      if (!siblings.length) return;

      var chipsHtml = '<div class="tall-owl-bogo-50-popup-offer__choices" role="group" aria-label="Pick the scent of your second one">';
      siblings.forEach(function (variant) {
        var selected = Number(variant.id) === Number(baseVariantId);
        var label = getVariantOptions(variant)[scentIdx] || variant.title;
        chipsHtml +=
          '<button type="button" class="tall-owl-bogo-50-popup-offer__choice' + (selected ? ' is-selected' : '') + '" data-bogo-50-scent-choice data-variant-id="' + variant.id + '" aria-pressed="' + (selected ? 'true' : 'false') + '">' +
          '<span class="tall-owl-bogo-50-popup-offer__choice-label">' + esc(label) + '</span>' +
          '<span class="tall-owl-bogo-50-popup-offer__choice-price">50% off</span>' +
          '</button>';
      });
      chipsHtml += '</div>';
      slot.innerHTML = chipsHtml;

      var subtext = mount.querySelector('[data-bogo-50-subtext]');
      if (subtext) {
        var addedTitle = cleanProductTitle(product.title || 'this item');
        subtext.textContent = 'Pick a scent for your ' + addedTitle + ' - your second one is 50% off.';
      }
    }

    function refreshShipBar(mount, cart) {
      if (!cart) return;
      var slot = mount.querySelector('[data-bogo-50-ship]');
      if (slot) slot.innerHTML = getShippingProgressHtml(cart);
    }

    function syncChipSelection(mount, variantId) {
      var chips = mount.querySelectorAll('[data-bogo-50-scent-choice]');
      Array.prototype.forEach.call(chips, function (chip) {
        var on = Number(chip.getAttribute('data-variant-id')) === Number(variantId);
        chip.classList.toggle('is-selected', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function renderConfirmState(mount, savingsCents, cart) {
      var shippingHtml = getShippingProgressHtml(cart);
      var savingsLabel = savingsCents > 0 ? formatMoney(savingsCents) : '';
      var titleHtml = savingsLabel
        ? 'Buy One, Get One 50% Off applied - you save <strong>' + esc(savingsLabel) + '</strong>'
        : 'Buy one, get one 50% off - mix and match any two eligible full-size products; Lip Balm excluded';
      var bodyText = savingsLabel
        ? 'Your second one is 50% off.'
        : 'Add a second eligible full-size product - the lower-priced one is 50% off, applied automatically.';

      mount.innerHTML =
        '<div class="tall-owl-bogo-50-popup-offer__backdrop" data-bogo-50-dismiss></div>' +
        '<section class="tall-owl-bogo-50-popup-offer__dialog" role="dialog" aria-modal="true" aria-label="Buy One, Get One 50% Off confirmation" tabindex="-1">' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__close" data-bogo-50-dismiss aria-label="Close Buy One, Get One 50% Off confirmation">&times;</button>' +
        '<div class="tall-owl-bogo-50-popup-offer__hero">' +
        '<div class="tall-owl-bogo-50-popup-offer__hero-copy">' +
        '<span class="tall-owl-bogo-50-popup-offer__badge">Buy One, Get One 50% Off</span>' +
        '<p class="tall-owl-bogo-50-popup-offer__title">' + titleHtml + '</p>' +
        '<p class="tall-owl-bogo-50-popup-offer__text">' + esc(bodyText) + '</p>' +
        '</div>' +
        '</div>' +
        shippingHtml +
        '<div class="tall-owl-bogo-50-popup-offer__actions">' +
        '<button type="button" class="btn tall-owl-bogo-50-popup-offer__add" data-bogo-50-view-cart>View cart</button>' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__dismiss" data-bogo-50-dismiss>Keep shopping</button>' +
        '</div>' +
        '</section>';

      mount.setAttribute('data-popup-state', 'confirm');
      mount.hidden = false;
      mount.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('tall-owl-bogo-50-popup-open');
      document.dispatchEvent(new CustomEvent('cart:close'));
      focusDialog(mount);
    }

    // ---- Pair reconciler: the single writer for free-one placement. ----
    // Idempotent, like the gift sync: fetch the cart, compute which product
    // groups have an odd unit count, and resolve them by INTENT:
    //   - units went UP (or fresh session): complete the pair - add the free
    //     one automatically, never manually.
    //   - units went DOWN and the shopper removed the $0 line: they opted out
    //     of the freebie - leave it removed (the cart nudge offers it back).
    //   - units went DOWN and the shopper removed a PAID line: the orphaned
    //     free unit would silently become full price, so it leaves too, with
    //     a toast.
    // Shopify splits a Buy X get Y pair into two cart lines (paid + $0), so
    // "which line did they touch" is the only reliable read of intent.
    var pairSyncBusy = false;
    var pairSyncTimer = null;
    var pairSyncQueued = false;
    var pendingAnnounce = null;
    var pairSyncPromise = Promise.resolve(); // latest reconcile chain; awaited before "View cart"
    var pairAddFailures = {}; // groupKey -> timestamp; out-of-stock cooldown
    var lastFreeLineRemovalAt = 0; // shopper clicked remove/minus on a $0 line
    var prevUnitsKey = 'tallOwlBogoPairUnits';

    function readPrevUnits() {
      try {
        var raw = readStorageValue(window.sessionStorage, prevUnitsKey);
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    }

    function writePrevUnits(map) {
      try {
        writeStorageValue(window.sessionStorage, prevUnitsKey, JSON.stringify(map));
      } catch (error) {
        /* non-critical */
      }
    }

    function changeByKey(lineKey, quantity) {
      return tallOwlQueueCartWrite(function () {
        var root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
        return fetch(root + 'cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: String(lineKey), quantity: Number(quantity) })
        }).then(function (response) {
          if (!response.ok) throw new Error('Cart change failed');
          return response.json();
        });
      });
    }

    function lineIsDiscounted(item) {
      var allocations = item.line_level_discount_allocations || [];
      return allocations.some(function (allocation) {
        return Number(allocation.amount || 0) > 0;
      });
    }

    function getGiftHandle() {
      var cfg = document.querySelector('[data-tall-owl-gift-config]');
      return cfg ? String(cfg.getAttribute('data-gift-handle') || '').toLowerCase() : '';
    }

    // Pair within product + size: the Admin discounts for sized products
    // (Whipped Tallow) are scoped per size, so cross-size pairs don't qualify.
    function groupKeyForItem(item) {
      var size = '';
      var opts = item.options_with_values || [];
      for (var i = 0; i < opts.length; i++) {
        if (/size|volume/i.test(String(opts[i].name || ''))) {
          size = String(opts[i].value || '');
          break;
        }
      }
      return String(item.product_id || item.variant_id) + '|' + size;
    }

    function computeGroups(cart) {
      var giftHandle = getGiftHandle();
      var groups = {};
      (cart.items || []).forEach(function (item) {
        if (item.gift_card) return;
        if (item.selling_plan_allocation) return;
        if (giftHandle && String(item.handle || '').toLowerCase() === giftHandle) return;
        var handle = String(item.handle || '').toLowerCase();
        if (handle.indexOf('sca_clone') !== -1 || handle.indexOf('freegift') !== -1) return;
        var key = groupKeyForItem(item);
        if (!groups[key]) groups[key] = { units: 0, newestItem: item, lines: [] }; // cart.js lists newest first
        groups[key].units += Number(item.quantity || 0);
        groups[key].lines.push(item);
      });
      return groups;
    }

    function schedulePairSync() {
      if (pairSyncTimer) window.clearTimeout(pairSyncTimer);
      pairSyncTimer = window.setTimeout(function () {
        pairSyncTimer = null;
        reconcilePairs();
      }, 180);
    }

    function reconcilePairs() {
      var mount = getMount();
      var enabled = isEnabled(mount);
      if (!enabled) return; // promo display off -> never place units either
      if (pairSyncBusy) {
        pairSyncQueued = true;
        return;
      }
      pairSyncBusy = true;

      var announce = pendingAnnounce;
      pendingAnnounce = null;
      var cartPromise = announce && announce.cart ? Promise.resolve(announce.cart) : fetchCart();

      pairSyncPromise = cartPromise
        .then(function (cart) {
          if (!cart) return null;
          var groups = computeGroups(cart);
          // A "+" on a paid BOGO line announces BEFORE the extra paid unit lands
          // (its group is still even). Re-defer so the scent popup fires once the
          // cart actually grows, instead of being consumed on an even group.
          if (announce && announce.fromPlus && announce.item) {
            var aGroup = groups[groupKeyForItem(announce.item)];
            if ((!aGroup || aGroup.units % 2 === 0) && announce.at && Date.now() - announce.at < 5000) {
              pendingAnnounce = announce;
              announce = null;
              window.setTimeout(schedulePairSync, 250);
            }
          }
          var announceKey = announce && announce.item ? groupKeyForItem(announce.item) : '';
          var now = Date.now();
          var prevUnits = readPrevUnits();
          var firstRun = prevUnits === null;

          var adds = [];
          var removals = [];
          Object.keys(groups).forEach(function (key) {
            var group = groups[key];
            if (group.units % 2 === 0) return;
            var isAnnounce = key === announceKey;
            var before = firstRun ? 0 : Number(prevUnits[key] || 0);

            if (isAnnounce || firstRun || group.units > before) {
              // Units went up: complete the pair with the free one.
              if (pairAddFailures[key] && now - pairAddFailures[key] < 60000) return;
              var sourceItem = isAnnounce ? announce.item : group.newestItem;
              adds.push({
                key: key,
                announce: isAnnounce,
                variantId: Number(sourceItem.variant_id || sourceItem.id) || 0
              });
              return;
            }

            if (group.units < before) {
              // Units went down. If the shopper just removed a $0 line, they
              // declined the freebie - leave the group as they made it.
              if (now - lastFreeLineRemovalAt < 10000) return;

              // They removed a paid line: take the orphaned free unit with it
              // so nobody is left with a surprise full-price item.
              var discountedLine = null;
              for (var i = 0; i < group.lines.length; i++) {
                if (lineIsDiscounted(group.lines[i])) { discountedLine = group.lines[i]; break; }
              }
              var lineToTrim = discountedLine || group.newestItem;
              removals.push({
                key: key,
                lineKey: lineToTrim.key,
                newQuantity: Math.max(0, Number(lineToTrim.quantity || 1) - 1)
              });
            }
          });

          // Snapshot the cart as it will look after our changes, so the next
          // run can tell up from down.
          var nextUnits = {};
          Object.keys(groups).forEach(function (key) { nextUnits[key] = groups[key].units; });
          adds.forEach(function (add) { nextUnits[add.key] = (nextUnits[add.key] || 0) + 1; });
          removals.forEach(function (removal) { nextUnits[removal.key] = Math.max(0, (nextUnits[removal.key] || 1) - 1); });
          writePrevUnits(nextUnits);

          if (!adds.length && !removals.length) {
            // Nothing to place. If this was an add event, confirm the savings.
            if (announce && announce.item && enabled) {
              var savings = getBogoSavings(cart, mount.getAttribute('data-discount-title') || 'BOGO');
              if (!(isDismissedState('confirm') && savings === 0)) {
                renderConfirmState(mount, savings, cart);
              }
            }
            return null;
          }

          // Announce FIRST so the popup is instant; the adds run behind it.
          var announceAdd = adds.filter(function (add) { return add.announce; })[0];
          if (announceAdd && enabled) {
            renderFreeAddedState(mount, announce.item, announceAdd.variantId, cart);
            if (announce.item.handle) {
              fetchProductByHandle(announce.item.handle).then(function (product) {
                fillScentChips(mount, product, announceAdd.variantId);
              });
            }
          }

          var chain = Promise.resolve();
          adds.forEach(function (add) {
            chain = chain.then(function () {
              return quietAdd(add.variantId)
                .catch(function () { return quietAdd(add.variantId); }) // one retry
                .catch(function () {
                  add.failed = true;
                  pairAddFailures[add.key] = Date.now();
                });
            });
          });
          removals.forEach(function (removal) {
            chain = chain.then(function () {
              return changeByKey(removal.lineKey, removal.newQuantity).catch(function () {
                removal.failed = true;
              });
            });
          });

          return chain.then(function () {
            if (announceAdd && announceAdd.failed && enabled) {
              var subtext = mount.querySelector('[data-bogo-50-subtext]');
              if (subtext) subtext.textContent = 'Your second one could not be added - it may be out of stock. The buy one, get one 50% off discount still applies when two are in the cart.';
              var chipSlot = mount.querySelector('[data-bogo-50-chips]');
              if (chipSlot) chipSlot.innerHTML = '';
            }
            var okAdds = adds.filter(function (add) { return !add.failed; });
            var okRemovals = removals.filter(function (removal) { return !removal.failed; });
            if (okAdds.length || okRemovals.length) {
              // Silent cart changes get a toast so they are never a mystery.
              if (typeof showToast === 'function') {
                if (!announceAdd && okAdds.length) {
                  showToast('We added your second one - buy one, get one 50% off.');
                } else if (okRemovals.length && !okAdds.length) {
                  showToast('Your free one went with it - the deal comes as a pair.');
                }
              }
              return notifyCartChanged().then(function (freshCart) {
                if (announceAdd && enabled && freshCart) refreshShipBar(mount, freshCart);
              });
            }
          });
        })
        .catch(function () { /* cart unavailable - try again on the next trigger */ })
        .then(function () {
          pairSyncBusy = false;
          if (pairSyncQueued) {
            pairSyncQueued = false;
            schedulePairSync();
          }
        });
    }

    // Is this added item a BOGO-eligible product? (The function still gates the
    // money server-side; this only decides whether to SHOW the offer.)
    function isEligibleItem(mount, item) {
      if (!item) return false;
      var handle = String(item.handle || (item.product && item.product.handle) || '').toLowerCase();
      var giftHandle = getGiftHandle();
      if (giftHandle && handle === giftHandle) return false; // the free gift, not BOGO
      if (!handle) return true; // unknown handle -> let it show; function still gates
      var eligible = ',' + String((mount && mount.getAttribute('data-suggested-handles')) || '').toLowerCase() + ',';
      if (eligible === ',,') return true; // no list configured
      return eligible.indexOf(',' + handle + ',') !== -1;
    }

    // Mix-and-match cross-sell: render up to 3 OTHER eligible products the
    // shopper can add to trigger the 50% off (the function discounts the cheaper
    // of the pair). Data comes from the hidden island rendered server-side.
    function fillPairSuggestions(mount, addedHandle) {
      var slot = mount.querySelector('[data-bogo-50-pair]');
      if (!slot) return;
      var island = document.querySelector('[data-bogo-50-suggestions]');
      if (!island) return;
      var nodes = island.querySelectorAll('[data-suggestion]');
      var added = String(addedHandle || '').toLowerCase();
      var picks = [];
      for (var i = 0; i < nodes.length && picks.length < 3; i++) {
        var h = String(nodes[i].getAttribute('data-handle') || '').toLowerCase();
        if (!h || h === added) continue; // don't suggest the one they just added
        picks.push(nodes[i]);
      }
      if (!picks.length) { slot.innerHTML = ''; return; }
      var html = '<ul class="tall-owl-bogo-50-popup-offer__pair-list" role="list">';
      picks.forEach(function (n) {
        var title = n.getAttribute('data-title') || '';
        var price = n.getAttribute('data-price') || '';
        var vid = n.getAttribute('data-variant-id') || '';
        var img = n.getAttribute('data-image') || '';
        var variants = [];
        try { variants = JSON.parse(n.getAttribute('data-variants') || '[]'); } catch (e) { variants = []; }

        // Multi-variant products (scent and/or size) get an inline picker; the
        // Add button's variant is kept in sync by the change handler below.
        var selectHtml = '';
        if (variants.length > 1) {
          selectHtml = '<select class="tall-owl-bogo-50-popup-offer__pair-select" data-bogo-50-pair-select aria-label="Choose an option for ' + esc(title) + '">';
          variants.forEach(function (v) {
            var sel = String(v.id) === String(vid) ? ' selected' : '';
            selectHtml += '<option value="' + esc(String(v.id)) + '" data-price="' + esc(v.price || '') + '"' + sel + '>' + esc(v.title) + '</option>';
          });
          selectHtml += '</select>';
        }

        html +=
          '<li class="tall-owl-bogo-50-popup-offer__pair-row">' +
          (img ? '<img class="tall-owl-bogo-50-popup-offer__pair-img" src="' + esc(img) + '" alt="" width="44" height="44" loading="lazy">' : '') +
          '<div class="tall-owl-bogo-50-popup-offer__pair-info">' +
          '<span class="tall-owl-bogo-50-popup-offer__pair-title">' + esc(title) + '</span>' +
          '<span class="tall-owl-bogo-50-popup-offer__pair-price" data-bogo-50-pair-price>' + esc(price) + '</span>' +
          selectHtml +
          '</div>' +
          '<button type="button" class="tall-owl-bogo-50-popup-offer__pair-cta" data-bogo-50-pair-add data-variant-id="' + esc(vid) + '" aria-label="Add ' + esc(title) + ' at 50% off">Add</button>' +
          '</li>';
      });
      html += '</ul>';
      slot.innerHTML = html;
    }

    // Show the opt-in offer (step 1). The chosen sub-step fetches its own data.
    function showOffer(mount, addedItem, baseVariantId, knownCart) {
      var handle = addedItem && (addedItem.handle || (addedItem.product && addedItem.product.handle));
      var cartPromise = knownCart ? Promise.resolve(knownCart) : fetchCart();
      cartPromise.then(function (cart) {
        currentOffer = {
          item: addedItem,
          title: (addedItem && (addedItem.product_title || addedItem.title)) || 'this item',
          image: getAddedItemImage(addedItem),
          baseVariantId: baseVariantId,
          handle: handle,
          cart: cart || null
        };
        renderOfferChoice(mount);
      });
    }

    // Add events: OFFER a second at 50% off (opt-in). We never write the cart
    // automatically - the shopper adds their second one from the popup.
    function maybeShow(addedItem, knownCart) {
      var mount = getMount();
      if (!isEnabled(mount)) { hideMount(mount); return; }

      var addedVariantId = Number(addedItem && (addedItem.variant_id || addedItem.id)) || 0;
      if (!addedVariantId) return;

      // One add can fire two theme events; handle it exactly once.
      var now = Date.now();
      if (recentAutoAdds[addedVariantId] && now - recentAutoAdds[addedVariantId] < 1500) return;
      recentAutoAdds[addedVariantId] = now;

      if (!isEligibleItem(mount, addedItem)) return; // not a BOGO product -> no offer
      showOffer(mount, addedItem, addedVariantId, knownCart);
    }

    document.addEventListener('product-form:submit:success', function (event) {
      var detail = event.detail || {};
      // This event ships the post-add cart - no extra fetch needed.
      maybeShow(detail.response || null, detail.cart || null);
    });

    document.addEventListener('ajaxProduct:added', function (event) {
      var product = event.detail && event.detail.product ? event.detail.product : null;
      maybeShow(product, null);
    });

    document.addEventListener('click', function (event) {
      // Step 1 -> step 2 navigation (and back). Re-renders from currentOffer.
      var chooseSame = event.target.closest('[data-bogo-50-choose-same]');
      if (chooseSame) {
        var cSameMount = chooseSame.closest(selector);
        if (cSameMount) renderScentStep(cSameMount);
        return;
      }
      var chooseDifferent = event.target.closest('[data-bogo-50-choose-different]');
      if (chooseDifferent) {
        var cDiffMount = chooseDifferent.closest(selector);
        if (cDiffMount) renderPairStep(cDiffMount);
        return;
      }
      var backButton = event.target.closest('[data-bogo-50-back]');
      if (backButton) {
        var backMount = backButton.closest(selector);
        if (backMount) renderOfferChoice(backMount);
        return;
      }

      // Opt-in: add the chosen second unit at 50% off, then confirm the savings.
      var addSecondButton = event.target.closest('[data-bogo-50-add-second]');
      if (addSecondButton) {
        var addMount = addSecondButton.closest(selector);
        if (!addMount) return;
        var desiredVariant = Number(addMount.getAttribute('data-bogo-50-desired-variant'))
          || Number(addMount.getAttribute('data-bogo-50-free-variant'));
        if (!desiredVariant) return;
        addSecondButton.disabled = true;
        quietAdd(desiredVariant)
          .catch(function () { return quietAdd(desiredVariant); }) // one retry
          .then(function () { return notifyCartChanged(); })
          .then(function (freshCart) {
            var savings = getBogoSavings(freshCart, addMount.getAttribute('data-discount-title') || 'BOGO');
            renderConfirmState(addMount, savings, freshCart);
          })
          .catch(function () {
            addSecondButton.disabled = false;
            if (typeof showToast === 'function') showToast('Could not add your second one - please try again.');
          });
        return;
      }

      // Mix-and-match: add a DIFFERENT product at 50% off, then confirm.
      var pairAddButton = event.target.closest('[data-bogo-50-pair-add]');
      if (pairAddButton) {
        var pairMount = pairAddButton.closest(selector);
        if (!pairMount) return;
        // Prefer the row's scent/size picker (guards against a change event that
        // hasn't fired yet on a fast tap); fall back to the button's default.
        var pairRow = pairAddButton.closest('.tall-owl-bogo-50-popup-offer__pair-row');
        var pairSelect = pairRow ? pairRow.querySelector('[data-bogo-50-pair-select]') : null;
        var pairVariant = Number(pairSelect && pairSelect.value ? pairSelect.value : pairAddButton.getAttribute('data-variant-id'));
        if (!pairVariant) return;
        pairAddButton.disabled = true;
        pairAddButton.classList.add('is-loading');
        quietAdd(pairVariant)
          .catch(function () { return quietAdd(pairVariant); }) // one retry
          .then(function () { return notifyCartChanged(); })
          .then(function (freshCart) {
            var savings = getBogoSavings(freshCart, pairMount.getAttribute('data-discount-title') || 'BOGO');
            renderConfirmState(pairMount, savings, freshCart);
          })
          .catch(function () {
            pairAddButton.disabled = false;
            pairAddButton.classList.remove('is-loading');
            if (typeof showToast === 'function') showToast('Could not add that one - please try again.');
          });
        return;
      }

      var viewCartButton = event.target.closest('[data-bogo-50-view-cart]');
      if (viewCartButton) {
        var viewMount = viewCartButton.closest(selector);
        var cartUrl = (window.theme && theme.routes && theme.routes.cartPage) || '/cart';
        var drawerEl = document.getElementById('CartDrawer');
        tallOwlSuppressGiftPopup = true; // heading to the cart - don't flash the gift popup
        hideMount(viewMount);
        // The cart drawer updates IN PLACE as the queued writes land (via
        // cart:build), so open it immediately - no need to wait. Only the
        // full-page fallback waits for pending writes (so the page isn't stale).
        function goToCartPage() {
          Promise.race([
            Promise.all([
              Promise.resolve(pairSyncPromise).catch(function () {}),
              Promise.resolve(tallOwlCartWriteChain).catch(function () {})
            ]),
            new Promise(function (resolve) { window.setTimeout(resolve, 2000); })
          ]).then(function () { window.location.href = cartUrl; });
        }
        if (!drawerEl) {
          goToCartPage();
          return;
        }
        // Open the theme's cart drawer via its "cart:open" event (theme.js,
        // CartDrawer.init). Deferred to the next tick so THIS click finishes
        // bubbling first - otherwise the drawer's outside-click handler (bound
        // the moment it opens) catches this same click and instantly re-closes
        // it (the theme only stopPropagation()s when opened via the bag icon).
        // Opening sets `js-drawer-open` on <html> synchronously; if it's not set
        // this template has no drawer (e.g. the cart page) so go to the page.
        window.setTimeout(function () {
          document.dispatchEvent(new CustomEvent('cart:open'));
          if (!document.documentElement.classList.contains('js-drawer-open')) {
            goToCartPage();
          }
        }, 0);
        return;
      }

      // Scent chip: swap the free unit to the chosen scent (same product + size).
      // The UI updates instantly; cart writes are serialized in the background
      // and rapid clicks converge on the last selection.
      var scentChoice = event.target.closest('[data-bogo-50-scent-choice]');
      if (scentChoice) {
        var chipMount = scentChoice.closest(selector);
        if (!chipMount) return;
        var pickedVariantId = Number(scentChoice.getAttribute('data-variant-id'));
        if (!pickedVariantId) return;

        chipMount.setAttribute('data-bogo-50-desired-variant', String(pickedVariantId));
        syncChipSelection(chipMount, pickedVariantId);

        // In the scent step nothing is in the cart yet - the chip only picks
        // which variant "Add at 50% off" will add. No cart write here.
        if (chipMount.getAttribute('data-popup-state') === 'offer-scent') return;

        swapChain = swapChain
          .then(function () {
            var desired = Number(chipMount.getAttribute('data-bogo-50-desired-variant'));
            var current = Number(chipMount.getAttribute('data-bogo-50-free-variant'));
            if (!desired || desired === current) return null; // stale click, already there

            pairSyncBusy = true; // hold the reconciler off mid-swap
            return fetchCart().then(function (cart) {
              var items = cart.items || [];
              // Shopify splits a Buy X get Y pair into separate lines (paid +
              // $0) that share a variant id - trim ONE unit from the $0 line,
              // never the whole variant.
              var fromLines = items.filter(function (item) { return Number(item.variant_id) === current; });
              var fromLine = fromLines.filter(lineIsDiscounted)[0] || fromLines[fromLines.length - 1];
              var step = fromLine
                ? changeByKey(fromLine.key, Math.max(0, Number(fromLine.quantity || 1) - 1))
                : Promise.resolve();
              return step.then(function () { return quietAdd(desired); }).then(function () {
                chipMount.setAttribute('data-bogo-50-free-variant', String(desired));
                return notifyCartChanged();
              });
            });
          })
          .then(function (cart) {
            if (cart) refreshShipBar(chipMount, cart);
          })
          .catch(function () {
            // Put the UI back on what's really in the cart.
            syncChipSelection(chipMount, Number(chipMount.getAttribute('data-bogo-50-free-variant')));
            chipMount.setAttribute('data-bogo-50-desired-variant', chipMount.getAttribute('data-bogo-50-free-variant') || '');
            if (typeof showToast === 'function') showToast('Could not switch the scent - please try again.');
          })
          .then(function () {
            pairSyncBusy = false;
            if (pairSyncQueued) {
              pairSyncQueued = false;
              schedulePairSync();
            }
          });
        return;
      }

      var dismissButton = event.target.closest('[data-bogo-50-dismiss]');
      if (dismissButton) {
        var dismissMount = dismissButton.closest(selector);
        var state = dismissMount ? dismissMount.getAttribute('data-popup-state') : '';
        markStateDismissed(state);
        hideMount(dismissMount);
      }
    });

    // Keep a pair row's Add button + price in sync with its scent/size picker.
    document.addEventListener('change', function (event) {
      var pairSelect = event.target.closest('[data-bogo-50-pair-select]');
      if (!pairSelect) return;
      var row = pairSelect.closest('.tall-owl-bogo-50-popup-offer__pair-row');
      if (!row) return;
      var opt = pairSelect.options[pairSelect.selectedIndex];
      if (!opt) return;
      var addBtn = row.querySelector('[data-bogo-50-pair-add]');
      if (addBtn && opt.value) addBtn.setAttribute('data-variant-id', opt.value);
      var priceEl = row.querySelector('[data-bogo-50-pair-price]');
      var priceText = opt.getAttribute('data-price');
      if (priceEl && priceText) priceEl.textContent = priceText;
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var mount = getMount();
      if (!mount || mount.hidden) return;
      var state = mount.getAttribute('data-popup-state');
      markStateDismissed(state);
      hideMount(mount);
    });

    // NOTE: the old silent "pair reconciler" (auto-adding a second unit on cart
    // edits / drawer opens / page load) is intentionally NOT wired up under
    // BOGO 50. The promo is now OPT-IN: the only cart write is the shopper's own
    // click on "Add at 50% off". The reconcilePairs()/schedulePairSync machinery
    // above is left defined but unused (kept for reference / possible rollback).

    // Keep the post-add experience as the scent popup, not the cart drawer:
    // suppress the theme's drawer auto-open on add-to-cart (the popup's "View
    // cart" is how the shopper opens the drawer). Flag is set in CAPTURE phase
    // (before the theme's bubble open handler); the drawer dispatches
    // drawerOpen.CartDrawer SYNCHRONOUSLY inside open(), so closing there is in
    // the same task and the open state never paints (no flash). Promo only.
    var suppressNextDrawerOpen = false;
    document.addEventListener(
      'ajaxProduct:added',
      function () {
        if (isEnabled(getMount())) {
          suppressNextDrawerOpen = true;
          window.setTimeout(function () { suppressNextDrawerOpen = false; }, 0);
        }
      },
      true
    );
    document.addEventListener('drawerOpen.CartDrawer', function () {
      if (suppressNextDrawerOpen) {
        suppressNextDrawerOpen = false;
        document.dispatchEvent(new CustomEvent('cart:close'));
      }
    });

    // No page-load auto-add: BOGO 50 never writes the cart on its own.
  }

  function getWalletSignals(element) {
    if (!element) return '';

    var parts = [];
    var seen = {};

    function push(value) {
      var token = normalizeText(value);
      if (!token || seen[token]) return;
      seen[token] = true;
      parts.push(token);
    }

    function collectFromNode(node) {
      if (!node || node.nodeType !== 1 || !node.getAttribute) return;
      ['aria-label', 'title', 'name', 'data-testid', 'data-provider', 'data-payment-method', 'alt', 'id', 'class'].forEach(
        function (attr) {
          push(node.getAttribute(attr));
        }
      );
      if (node.tagName && node.tagName.toLowerCase() === 'iframe') {
        push(node.getAttribute('src'));
      }
    }

    collectFromNode(element);

    var descendants = element.querySelectorAll
      ? element.querySelectorAll('[aria-label],[title],[name],[data-testid],[data-provider],[data-payment-method],[alt],[id],[class],iframe')
      : [];
    var maxDescendants = Math.min(descendants.length, 120);
    for (var i = 0; i < maxDescendants; i += 1) {
      collectFromNode(descendants[i]);
    }

    push(element.textContent || '');
    push(element.innerHTML || '');

    return parts.join(' ');
  }

  function detectWalletProvider(element) {
    var haystack = getWalletSignals(element);
    if (!haystack) return 'unknown';

    if (
      haystack.indexOf('shop pay') !== -1 ||
      haystack.indexOf('shop-pay') !== -1 ||
      haystack.indexOf('shop_pay') !== -1 ||
      haystack.indexOf('shoppay') !== -1 ||
      haystack.indexOf('shopify pay') !== -1
    ) {
      return 'shop';
    }

    if (
      haystack.indexOf('paypal') !== -1 ||
      haystack.indexOf('pay pal') !== -1 ||
      haystack.indexOf('paypal.com') !== -1 ||
      haystack.indexOf('paypalobjects') !== -1
    ) {
      return 'paypal';
    }

    if (
      haystack.indexOf('amazon') !== -1 ||
      haystack.indexOf('amzn') !== -1 ||
      haystack.indexOf('amazon pay') !== -1
    ) {
      return 'amazon';
    }

    if (
      haystack.indexOf('google pay') !== -1 ||
      haystack.indexOf('googlepay') !== -1 ||
      haystack.indexOf('gpay') !== -1 ||
      haystack.indexOf('pay.google') !== -1
    ) {
      return 'google';
    }

    if (haystack.indexOf('apple pay') !== -1 || haystack.indexOf('applepay') !== -1) {
      return 'apple';
    }

    return 'other';
  }

  function getCartDrawerWalletContainers(drawer) {
    if (!drawer) return [];

    var nested = Array.prototype.slice.call(
      drawer.querySelectorAll('.additional-checkout-buttons [data-shopify-buttoncontainer]')
    );
    if (nested.length) {
      return nested;
    }

    return Array.prototype.slice
      .call(drawer.querySelectorAll('.additional-checkout-buttons'))
      .filter(function (container) {
        return !!container;
      });
  }

  function getWalletCandidates(container) {
    if (!container || !container.children) return [];
    return Array.prototype.filter.call(container.children, function (child) {
      if (!child || child.nodeType !== 1) return false;
      var tag = (child.tagName || '').toLowerCase();
      return tag !== 'script' && tag !== 'style' && tag !== 'template';
    });
  }

  function setWalletVisible(node, visible) {
    if (!node) return;
    node.classList.toggle('tall-owl-wallet-hidden', !visible);
    if (visible) {
      node.removeAttribute('aria-hidden');
      node.style.removeProperty('display');
      return;
    }
    node.setAttribute('aria-hidden', 'true');
    node.style.setProperty('display', 'none', 'important');
  }

  function filterCartDrawerWalletButtons() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    var containers = getCartDrawerWalletContainers(drawer);
    if (!containers.length) return;

    containers.forEach(function (container) {
      container.classList.add('tall-owl-wallet-container');

      var candidates = getWalletCandidates(container);
      if (!candidates.length) return;

      var shopNode = null;
      var paypalNode = null;
      var allowedFallback = [];

      candidates.forEach(function (candidate) {
        var provider = detectWalletProvider(candidate);
        candidate.setAttribute('data-tall-owl-wallet-provider', provider);

        if (provider === 'shop' && !shopNode) {
          shopNode = candidate;
        } else if (provider === 'paypal' && !paypalNode) {
          paypalNode = candidate;
        } else if (provider !== 'amazon' && provider !== 'google' && provider !== 'apple') {
          allowedFallback.push(candidate);
        }
      });

      var keep = [];
      if (shopNode) keep.push(shopNode);
      if (paypalNode && keep.indexOf(paypalNode) === -1) keep.push(paypalNode);

      if (keep.length < 2) {
        allowedFallback.forEach(function (candidate) {
          if (keep.length >= 2) return;
          if (keep.indexOf(candidate) !== -1) return;
          keep.push(candidate);
        });
      }

      if (keep.length < 2) {
        candidates.forEach(function (candidate) {
          if (keep.length >= 2) return;
          if (keep.indexOf(candidate) !== -1) return;
          keep.push(candidate);
        });
      }

      keep = keep.slice(0, 2);

      candidates.forEach(function (candidate) {
        var visible = keep.indexOf(candidate) !== -1;
        setWalletVisible(candidate, visible);
        candidate.style.order = visible ? String(keep.indexOf(candidate) + 1) : '';
      });
    });
  }

  function scheduleCartDrawerWalletFilter() {
    [0, 120, 360, 900].forEach(function (delay) {
      window.setTimeout(filterCartDrawerWalletButtons, delay);
    });
  }

  function initCartDrawerWalletButtons() {
    scheduleCartDrawerWalletFilter();

    var drawer = document.getElementById('CartDrawer');
    if (!drawer || !window.MutationObserver) return;

    var observer = new MutationObserver(function () {
      scheduleCartDrawerWalletFilter();
    });

    observer.observe(drawer, {
      childList: true,
      subtree: true
    });

    document.addEventListener('cart:updated', function () {
      scheduleCartDrawerWalletFilter();
    });

    document.addEventListener('drawerOpen.CartDrawer', function () {
      scheduleCartDrawerWalletFilter();
    });

    window.addEventListener('load', function () {
      scheduleCartDrawerWalletFilter();
    });
  }

  function submitKlaviyoJsonp(listId, email, source, onSuccess, onError) {
    var callbackName = 'tallOwlKlaviyo_' + Date.now() + '_' + Math.floor(Math.random() * 10000);
    var script = document.createElement('script');
    var cleaned = false;

    function cleanup() {
      if (cleaned) return;
      cleaned = true;
      if (script.parentNode) script.parentNode.removeChild(script);
      try {
        delete window[callbackName];
      } catch (error) {
        window[callbackName] = undefined;
      }
    }

    window[callbackName] = function (response) {
      cleanup();
      if (response && (response.success || response.data)) {
        onSuccess();
      } else {
        onError();
      }
    };

    script.async = true;
    script.src =
      'https://manage.kmail-lists.com/ajax/subscriptions/subscribe?callback=' +
      encodeURIComponent(callbackName) +
      '&g=' +
      encodeURIComponent(listId) +
      '&email=' +
      encodeURIComponent(email) +
      '&$fields=' +
      encodeURIComponent('$source') +
      '&$source=' +
      encodeURIComponent(source || 'homepage-newsletter');

    script.onerror = function () {
      cleanup();
      onError();
    };

    window.setTimeout(function () {
      if (!cleaned) {
        cleanup();
        onError();
      }
    }, 10000);

    document.head.appendChild(script);
  }

  function initKlaviyoNewsletterForms() {
    var forms = document.querySelectorAll('form[data-klaviyo-form]');
    if (!forms.length) return;

    forms.forEach(function (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();

        var listId = form.getAttribute('data-klaviyo-list-id') || '';
        var source = form.getAttribute('data-newsletter-source') || 'homepage-newsletter';
        var emailInput = form.querySelector('input[type="email"]');
        var submitBtn = form.querySelector('button[type="submit"]');
        var status = form.querySelector('[data-newsletter-status]');
        var email = emailInput ? emailInput.value.trim() : '';
        var emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        if (!emailIsValid || !listId) {
          if (status) {
            status.textContent = 'Please enter a valid email address and try again.';
            status.classList.remove('tall-owl-newsletter__status--success');
            status.classList.add('tall-owl-newsletter__status--error');
          }
          return;
        }

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.setAttribute('aria-busy', 'true');
        }
        if (status) {
          status.textContent = 'Submitting...';
          status.classList.remove('tall-owl-newsletter__status--success', 'tall-owl-newsletter__status--error');
        }

        submitKlaviyoJsonp(
          listId,
          email,
          source,
          function () {
            if (status) {
              status.textContent = 'Success. Check your inbox for your welcome email.';
              status.classList.remove('tall-owl-newsletter__status--error');
              status.classList.add('tall-owl-newsletter__status--success');
            }
            form.reset();
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.removeAttribute('aria-busy');
            }
          },
          function () {
            if (status) {
              status.textContent = 'Unable to subscribe right now. Please try again.';
              status.classList.remove('tall-owl-newsletter__status--success');
              status.classList.add('tall-owl-newsletter__status--error');
            }
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.removeAttribute('aria-busy');
            }
          }
        );
      });
    });
  }

  function normalizeInputModes() {
    document.querySelectorAll('input[type="email"]:not([inputmode])').forEach(function (el) {
      el.setAttribute('inputmode', 'email');
    });
    document.querySelectorAll('input[type="tel"]:not([inputmode])').forEach(function (el) {
      el.setAttribute('inputmode', 'tel');
    });
    document.querySelectorAll('input[type="search"]:not([inputmode])').forEach(function (el) {
      el.setAttribute('inputmode', 'search');
    });
    document.querySelectorAll('input[type="url"]:not([inputmode])').forEach(function (el) {
      el.setAttribute('inputmode', 'url');
    });
  }

  function buildContactCreationDate(now) {
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      }).format(now);
    } catch (error) {
      return now.toISOString();
    }
  }

  function applyContactCreationDateFields(form) {
    if (!form || !form.querySelectorAll) return;
    var now = new Date();
    var formatted = buildContactCreationDate(now);
    var iso = now.toISOString();

    Array.prototype.forEach.call(form.querySelectorAll('[data-contact-creation-date]'), function (input) {
      input.value = formatted;
    });

    Array.prototype.forEach.call(form.querySelectorAll('[data-contact-creation-date-iso]'), function (input) {
      input.value = iso;
    });
  }

  function initContactCreationDateFields() {
    Array.prototype.forEach.call(document.querySelectorAll('form'), function (form) {
      if (!form.querySelector('[data-contact-creation-date], [data-contact-creation-date-iso]')) return;
      applyContactCreationDateFields(form);
    });

    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !form.querySelector) return;
        if (!form.querySelector('[data-contact-creation-date], [data-contact-creation-date-iso]')) return;
        applyContactCreationDateFields(form);
      },
      true
    );
  }

  function collapseEmptyHomepageSections() {
    if (!document.body.classList.contains('template-index')) return;

    var wrappers = document.querySelectorAll('.main-content .shopify-section');
    wrappers.forEach(function (wrapper) {
      var visibleChildren = Array.prototype.filter.call(wrapper.children, function (child) {
        var tag = child.tagName;
        return tag !== 'STYLE' && tag !== 'SCRIPT';
      });
      if (!visibleChildren.length) {
        wrapper.setAttribute('hidden', 'hidden');
      }
    });
  }

  function initRevealAnimations() {
    var sections = Array.prototype.slice.call(document.querySelectorAll('.tall-owl-section'));
    if (!sections.length) return;

    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    sections.forEach(function (section) {
      section.setAttribute('data-tall-owl-reveal', 'true');
      if (reducedMotion) {
        section.classList.add('is-revealed');
      }
    });

    if (reducedMotion || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }

  function applyModalAccessibility() {
    document.querySelectorAll('.modal').forEach(function (modal) {
      if (!modal.hasAttribute('role')) modal.setAttribute('role', 'dialog');
      if (!modal.hasAttribute('aria-modal')) modal.setAttribute('aria-modal', 'true');
    });
  }

  function initQuickSheetSwipeDismiss() {
    var active = null;

    document.addEventListener(
      'touchstart',
      function (event) {
        var content = event.target.closest('.tall-owl-quick-sheet .modal__centered-content');
        if (!content) return;
        if (content.scrollTop > 0) return;
        active = {
          content: content,
          startY: event.touches[0].clientY,
          deltaY: 0
        };
      },
      { passive: true }
    );

    document.addEventListener(
      'touchmove',
      function (event) {
        if (!active) return;
        var y = event.touches[0].clientY;
        active.deltaY = Math.max(0, y - active.startY);
        if (active.deltaY > 0) {
          active.content.style.transform = 'translateY(' + Math.min(active.deltaY, 140) + 'px)';
        }
      },
      { passive: true }
    );

    document.addEventListener(
      'touchend',
      function () {
        if (!active) return;
        var content = active.content;
        var deltaY = active.deltaY;
        content.style.transform = '';
        if (deltaY > 80) {
          var modal = content.closest('.tall-owl-quick-sheet');
          var closeBtn = modal && modal.querySelector('.js-modal-close');
          if (closeBtn) closeBtn.click();
        }
        active = null;
      },
      { passive: true }
    );
  }

  document.addEventListener('ajaxProduct:added', function (event) {
    var product = event.detail && event.detail.product ? event.detail.product : null;
    var title = product && product.title ? product.title : 'Item';
    showToast(title + ' added to cart.');
  });

  document.addEventListener('shopify:section:load', function (event) {
    initVariantDescriptionBlocks(event && event.target ? event.target : document);
    initTallOwlPdpPairModules(event && event.target ? event.target : document);
  });

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.tall-owl-mobile-atc, .tall-owl-card-atc');
    if (btn) {
      event.preventDefault();
      if (btn.disabled) return;

      var previousText = (btn.textContent || '').trim() || 'Add to Cart';
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'Adding...';

      addToCart(btn.getAttribute('data-variant-id'))
        .catch(function () {
          /* addToCart already handles user-facing error messaging */
        })
        .then(function () {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
          btn.textContent = previousText;
        });
      return;
    }

  });

  // Tall Owl legacy promo-app compatibility flag.
  // When the theme enables the promo app (settings.tall_owl_promo_app_enabled),
  // a mount node is rendered by tall-owl-promo-app-flag.liquid. We then stamp the
  // cart with `_promo_app=on` so the compatibility/rollback version of the
  // legacy Function remains usable during cutover. The unified 2026-04 Function
  // is server-authoritative on every channel and does not depend on this flag.
  function initTallOwlPromoFlag() {
    var mount = document.querySelector('[data-tall-owl-promo-app]');
    if (!mount || mount.getAttribute('data-enabled') !== 'true') return;

    var FLAG_KEY = '_promo_app';
    var FLAG_VALUE = 'on';

    fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        // Idempotent: skip the write if the flag is already set, so we don't
        // POST /cart/update.js on every page load.
        var current = cart && cart.attributes ? cart.attributes[FLAG_KEY] : null;
        if (current === FLAG_VALUE) return null;

        var body = { attributes: {} };
        body.attributes[FLAG_KEY] = FLAG_VALUE;
        return tallOwlQueueCartWrite(function () {
          return fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          });
        });
      })
      .catch(function () {
        // Non-critical: if the flag can't be set the promos simply stay off.
      });
  }

  // Tall Owl free Lip Balm gift - auto add/remove + scent picker.
  // Mirrors the server-side tall-owl-promos threshold: when the merchandise
  // subtotal EXCLUDING the gift reaches the threshold, the gift variant is added
  // at its required $0 Admin price; below the threshold it is removed. Checkout
  // validation is the final eligibility/price backstop. Scent is changed via the
  // <select> rendered on the gift line in cart-item.liquid.
  function initTallOwlGiftPicker() {
    var config = document.querySelector('[data-tall-owl-gift-config]');
    if (!config || config.getAttribute('data-enabled') !== 'true') return;
    if (document.documentElement.hasAttribute('data-tall-owl-gift-bound')) return;
    document.documentElement.setAttribute('data-tall-owl-gift-bound', 'true');

    var GIFT_HANDLE = String(config.getAttribute('data-gift-handle') || '').toLowerCase();
    var DEFAULT_VARIANT = Number(config.getAttribute('data-default-variant') || 0);
    var THRESHOLD_CENTS = Number(config.getAttribute('data-threshold') || 5000);
    if (!GIFT_HANDLE || !DEFAULT_VARIANT) return;

    var POPUP_SELECTOR = '[data-tall-owl-gift-popup]';
    var BOGO_SELECTOR = '[data-tall-owl-bogo-50-popup-offer]';
    var DISMISS_KEY = 'tallOwlGiftPopupDismissed';
    var busy = false;
    var syncPending = false;
    var syncTimer = null;
    var pendingScentVariant = 0;
    var refreshPending = false;
    var giftProductPromise = null;
    var pendingPopupVariant = 0;
    var bogoObserver = null;
    var GIFT_SYNC_RETRY_DELAYS = [250, 750, 1500];

    function isGiftItem(item) {
      return !!item && String(item.handle || '').toLowerCase() === GIFT_HANDLE;
    }

    function esc(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    function subtotalExGiftCents(cart) {
      var items = cart && Array.isArray(cart.items) ? cart.items : [];
      var giftLineTotal = items.reduce(function (sum, item) {
        if (!isGiftItem(item)) return sum;
        return sum + Number(item.final_line_price || 0);
      }, 0);
      var postDiscountCartTotal = Number(cart && cart.total_price);

      // cart.total_price includes order-level discounts that Shopify may leave
      // unallocated on cart lines. Subtract any invalid nonzero gift line so
      // only post-discount paid merchandise can qualify for the threshold.
      if (Number.isFinite(postDiscountCartTotal) && postDiscountCartTotal >= 0) {
        return Math.max(0, postDiscountCartTotal - giftLineTotal);
      }

      return items.reduce(function (sum, item) {
        if (isGiftItem(item)) return sum;
        return sum + Number(item.final_line_price || 0);
      }, 0);
    }

    function fetchCartJson() {
      return fetch('/cart.js', { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) {
            var error = new Error('Cart request failed with status ' + response.status);
            error.status = response.status;
            throw error;
          }
          return response.json();
        });
    }

    function queuedFetchCartJson() {
      return tallOwlQueueCartWrite(fetchCartJson);
    }

    function waitForGiftRetry(delay) {
      return new Promise(function (resolve) {
        window.setTimeout(resolve, delay);
      });
    }

    function fetchGiftProduct() {
      if (giftProductPromise) return giftProductPromise;
      var root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      giftProductPromise = fetch(root + 'products/' + encodeURIComponent(GIFT_HANDLE) + '.js', {
        method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' }
      })
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; });
      return giftProductPromise;
    }

    // Publish only a verified gift state. Gift-owned events are marked so they
    // refresh the drawer without recursively scheduling another reconciliation.
    function publishCartChanged(cart) {
      refreshPending = false;
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart, source: 'tall-owl-gift' }
      }));
      document.dispatchEvent(new CustomEvent('cart:build', {
        detail: { source: 'tall-owl-gift' }
      }));
      return cart;
    }

    function notifyCartChanged() {
      return queuedFetchCartJson()
        .then(verifyGiftState)
        .then(function (cart) {
          return publishCartChanged(cart);
        });
    }

    // Quiet add - deliberately does NOT dispatch ajaxProduct:added, so adding the
    // gift never triggers the BOGO popup (the gift isn't a BOGO item).
    function apiAddGift(variantId) {
      refreshPending = true;
      return tallOwlQueueCartWrite(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        }).then(function (response) {
          if (!response.ok) {
            var error = new Error('Gift add failed with status ' + response.status);
            error.status = response.status;
            throw error;
          }
          return response.json();
        });
      });
    }

    function apiChangeGift(key, quantity) {
      refreshPending = true;
      return tallOwlQueueCartWrite(function () {
        return theme.cart.changeItem(key, quantity);
      });
    }

    // ---- Scent popup: DORMANT since 2026-07-11 (kept for rollback). ----
    // The unlock flow no longer opens a modal - the drawer's gift line + a toast
    // replaced it. showGiftPopup has no caller and its mount is no longer
    // rendered by theme.liquid, so everything below is inert.
    function popupMount() { return document.querySelector(POPUP_SELECTOR); }
    function bogoOpen() { var m = document.querySelector(BOGO_SELECTOR); return !!m && !m.hidden; }
    function isDismissed() { return readStorageValue(window.sessionStorage, DISMISS_KEY) === '1'; }
    function markDismissed() { writeStorageValue(window.sessionStorage, DISMISS_KEY, '1'); }

    function hidePopup() {
      var m = popupMount();
      if (!m) return;
      m.innerHTML = '';
      m.hidden = true;
      m.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('tall-owl-gift-popup-open');
    }

    function renderPopup(product, currentVariantId) {
      var m = popupMount();
      if (!m) return;
      var variants = product && Array.isArray(product.variants) ? product.variants : [];
      var chips = variants
        .filter(function (v) { return v.available; })
        .map(function (v) {
          var selected = Number(v.id) === Number(currentVariantId);
          return '<button type="button" class="tall-owl-bogo-50-popup-offer__choice' + (selected ? ' is-selected' : '') + '" data-gift-scent-choice data-variant-id="' + v.id + '">' +
            '<span class="tall-owl-bogo-50-popup-offer__choice-label">' + esc(v.title) + '</span>' +
            '<span class="tall-owl-bogo-50-popup-offer__choice-price">Free</span>' +
            '</button>';
        })
        .join('');

      m.innerHTML =
        '<div class="tall-owl-bogo-50-popup-offer__backdrop" data-gift-dismiss></div>' +
        '<section class="tall-owl-bogo-50-popup-offer__dialog" role="dialog" aria-modal="true" aria-label="Free Lip Balm gift" tabindex="-1">' +
        '<button type="button" class="tall-owl-bogo-50-popup-offer__close" data-gift-dismiss aria-label="Close free gift offer">&times;</button>' +
        '<div class="tall-owl-bogo-50-popup-offer__hero"><div class="tall-owl-bogo-50-popup-offer__hero-copy">' +
        '<span class="tall-owl-bogo-50-popup-offer__badge">FREE GIFT</span>' +
        '<p class="tall-owl-bogo-50-popup-offer__title">You unlocked a free Lip Balm</p>' +
        '<p class="tall-owl-bogo-50-popup-offer__text">It is in your cart, on us. Pick your scent - you can change it anytime in the cart.</p>' +
        '</div></div>' +
        '<div class="tall-owl-bogo-50-popup-offer__choices" role="list" aria-label="Choose your scent">' + chips + '</div>' +
        '<div class="tall-owl-bogo-50-popup-offer__actions">' +
        '<button type="button" class="btn tall-owl-bogo-50-popup-offer__add" data-gift-dismiss>Done</button>' +
        '</div>' +
        '<p class="tall-owl-bogo-50-popup-offer__note">Spend $' + (THRESHOLD_CENTS / 100) + '+ to keep your free gift.</p>' +
        '</section>';

      m.hidden = false;
      m.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('tall-owl-gift-popup-open');
      document.dispatchEvent(new CustomEvent('cart:close'));
      window.setTimeout(function () {
        var dialog = m.querySelector('.tall-owl-bogo-50-popup-offer__dialog');
        if (dialog) dialog.focus({ preventScroll: true });
      }, 30);
    }

    function watchBogoClose() {
      if (bogoObserver) return;
      var bogo = document.querySelector(BOGO_SELECTOR);
      if (!bogo) return;
      bogoObserver = new MutationObserver(function () {
        if (bogoOpen() || !pendingPopupVariant) return;
        var variant = pendingPopupVariant;
        pendingPopupVariant = 0;
        showGiftPopup(variant);
      });
      bogoObserver.observe(bogo, { attributes: true, attributeFilter: ['hidden'] });
    }

    function showGiftPopup(currentVariantId) {
      if (isDismissed() || !popupMount() || tallOwlSuppressGiftPopup) return;
      // Don't stack on top of the BOGO popup - defer until it closes.
      if (bogoOpen()) {
        pendingPopupVariant = currentVariantId || DEFAULT_VARIANT;
        watchBogoClose();
        return;
      }
      fetchGiftProduct().then(function (product) {
        if (product) renderPopup(product, currentVariantId);
      });
    }

    // ---- Cart mechanics: auto add/remove + scent swap ----
    function enableGiftScentSelectors() {
      var selects = document.querySelectorAll('[data-tall-owl-gift-scent]');
      for (var index = 0; index < selects.length; index += 1) {
        selects[index].disabled = false;
      }
    }

    function finishGiftOperation() {
      busy = false;
      enableGiftScentSelectors();

      if (pendingScentVariant) {
        var variantId = pendingScentVariant;
        pendingScentVariant = 0;
        setGiftScent(variantId);
        return;
      }

      if (syncPending) {
        scheduleSync(0);
      }
    }

    function setGiftScent(variantId) {
      if (!variantId) return Promise.resolve(null);
      if (busy) {
        pendingScentVariant = variantId;
        syncPending = true;
        return Promise.resolve(null);
      }

      busy = true;
      return queuedFetchCartJson()
        .then(function (cart) {
          var line = cart && Array.isArray(cart.items) ? cart.items.filter(isGiftItem)[0] : null;
          if (!cart) throw new Error('Unable to read cart');
          if (line && Number(line.variant_id) === Number(variantId)) return cart;
          var chain = Promise.resolve();
          if (line) chain = chain.then(function () { return apiChangeGift(line.key, 0); });
          return chain.then(function () { return apiAddGift(variantId); }).then(notifyCartChanged);
        })
        .then(
          function (cart) {
            finishGiftOperation();
            return cart;
          },
          function () {
            syncPending = true;
            showToast('Unable to change your gift scent right now.');
            finishGiftOperation();
            return null;
          }
        );
    }

    function totalGiftQuantity(cart) {
      var items = cart && Array.isArray(cart.items) ? cart.items : [];
      return items.reduce(function (quantity, item) {
        return quantity + (isGiftItem(item) ? Number(item.quantity || 0) : 0);
      }, 0);
    }

    function verifyGiftState(cart) {
      if (!cart) throw new Error('Unable to verify cart');
      var qualifies = subtotalExGiftCents(cart) >= THRESHOLD_CENTS;
      var giftQuantity = totalGiftQuantity(cart);
      if ((qualifies && giftQuantity !== 1) || (!qualifies && giftQuantity !== 0)) {
        var error = new Error('Gift state did not match cart eligibility');
        error.retriable = true;
        throw error;
      }
      return cart;
    }

    function reconcileGiftOnce() {
      return queuedFetchCartJson()
        .then(function (cart) {
          if (!cart) throw new Error('Unable to read cart');
          var items = Array.isArray(cart.items) ? cart.items : [];
          var giftLines = items.filter(isGiftItem);
          var qualifies = subtotalExGiftCents(cart) >= THRESHOLD_CENTS;
          var giftLine = giftLines[0] || null;

          if (!qualifies && giftLines.length) {
            var chain = Promise.resolve();
            giftLines.forEach(function (line) {
              chain = chain.then(function () { return apiChangeGift(line.key, 0); });
            });
            return chain.then(notifyCartChanged).then(verifyGiftState);
          }

          if (qualifies && !giftLine) {
            return apiAddGift(DEFAULT_VARIANT)
              .then(notifyCartChanged)
              .then(verifyGiftState)
              .then(function (updatedCart) {
                // No modal on unlock: the cart drawer (which opens on add) shows
                // the gift line with its scent dropdown. A toast covers the cases
                // where the unlock happens without the drawer visible.
                if (typeof showToast === 'function') {
                  showToast('Free Lip Balm added - pick your scent in the cart.');
                }
                return updatedCart;
              });
          }

          // Collapse duplicate gift lines and quantities to exactly one unit.
          if (qualifies && (giftLines.length > 1 || Number(giftLine.quantity || 0) !== 1)) {
            var normalizeChain = Promise.resolve();
            if (Number(giftLine.quantity || 0) !== 1) {
              normalizeChain = normalizeChain.then(function () {
                return apiChangeGift(giftLine.key, 1);
              });
            }
            giftLines.slice(1).forEach(function (line) {
              normalizeChain = normalizeChain.then(function () {
                return apiChangeGift(line.key, 0);
              });
            });
            return normalizeChain.then(notifyCartChanged).then(verifyGiftState);
          }

          var verifiedCart = verifyGiftState(cart);
          return refreshPending ? publishCartChanged(verifiedCart) : verifiedCart;
        });
    }

    function reconcileGift(attempt) {
      return reconcileGiftOnce().catch(function (error) {
        var status = Number(error && error.status || 0);
        var retriable = !!(error && error.retriable) || !status || status === 429 || status >= 500;
        if (!retriable || attempt >= GIFT_SYNC_RETRY_DELAYS.length) throw error;
        return waitForGiftRetry(GIFT_SYNC_RETRY_DELAYS[attempt])
          .then(function () { return reconcileGift(attempt + 1); });
      });
    }

    function syncGift() {
      if (busy) {
        syncPending = true;
        return Promise.resolve(null);
      }

      busy = true;
      return reconcileGift(0).then(
        function (cart) {
          finishGiftOperation();
          return cart;
        },
        function (error) {
          if (window.console && typeof window.console.warn === 'function') {
            window.console.warn('[Tall Owl] Free gift cart sync failed.', error);
          }
          finishGiftOperation();
          return null;
        }
      );
    }

    function scheduleSync(delay) {
      syncPending = true;
      if (busy) return;
      if (syncTimer) window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(function () {
        syncTimer = null;
        syncPending = false;
        syncGift();
      }, typeof delay === 'number' ? delay : 180);
    }

    // Cart-line dropdown change ? swap scent.
    document.addEventListener('change', function (event) {
      var select = event.target.closest('[data-tall-owl-gift-scent]');
      if (!select) return;
      var newId = Number(select.value);
      if (!newId) return;
      select.disabled = true;
      setGiftScent(newId);
    });

    // Popup interactions: pick a scent chip, or dismiss.
    document.addEventListener('click', function (event) {
      var choice = event.target.closest('[data-gift-scent-choice]');
      if (choice) {
        event.preventDefault();
        var variantId = Number(choice.getAttribute('data-variant-id'));
        hidePopup();
        if (variantId) setGiftScent(variantId);
        return;
      }
      var dismiss = event.target.closest('[data-gift-dismiss]');
      if (dismiss) {
        event.preventDefault();
        markDismissed();
        hidePopup();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var m = popupMount();
      if (!m || m.hidden) return;
      markDismissed();
      hidePopup();
    });

    function handleGiftCartEvent(event) {
      if (event && event.detail && event.detail.source === 'tall-owl-gift') return;
      scheduleSync();
    }

    document.addEventListener('cart:updated', handleGiftCartEvent);
    document.addEventListener('cart:build', handleGiftCartEvent);
    document.addEventListener('ajaxProduct:added', handleGiftCartEvent);
    document.addEventListener('drawerOpen.CartDrawer', handleGiftCartEvent);

    syncGift();
  }

  normalizeInputModes();
  initContactCreationDateFields();
  collapseEmptyHomepageSections();
  initRevealAnimations();
  initKlaviyoNewsletterForms();
  initCollectionGridRatings(document);
  initRotatingScentCards(document);
  initCollectionCardForms(document);
  initCollectionGridRatingsObserver();
  initVariantDescriptionBlocks(document);
  initVariantDescriptionObserver();
  initQuickSheetSwipeDismiss();
  initCartDrawerScrollReset();
  initCartRemoveLinkGuard();
  initTallOwlCartVariantSwap();
  initTallOwlCartCrossSellAdd();
  initTallOwlPdpPairModules(document);
  normalizeTallOwlFullCart();
  initTallOwlBogo50PopupOffer();
  initTallOwlPromoFlag();
  initTallOwlGiftPicker();
  document.addEventListener('cart:markup:updated', normalizeTallOwlFullCart);
  // ---------- Size image filtering removed ----------
  // Image ordering + scent-based image sets handle everything natively.

  // ---------- Cart "You might also like" carousel ----------
  (function initCartYmal() {
    var formatter = window.Shopify && Shopify.formatMoney ? Shopify.formatMoney : null;
    var moneyFormat = window.theme && theme.settings ? theme.settings.moneyFormat : '${{amount}}';
    var refreshTimer = null;

    function formatMoney(cents) {
      if (formatter) return formatter(cents, moneyFormat);
      return '$' + (cents / 100).toFixed(2);
    }

    function esc(str) {
      return String(str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    function buildOptionSelects(product) {
      var options = product.options || [];
      var variants = product.variants || [];
      if (!variants.length || (options.length === 1 && options[0] === 'Title')) return '';

      var html = '';
      for (var i = 0; i < options.length; i++) {
        var optionName = options[i];
        /* Collect unique values for this option position */
        var seen = {};
        var values = [];
        for (var j = 0; j < variants.length; j++) {
          var val = variants[j]['option' + (i + 1)] || '';
          if (val && !seen[val]) {
            seen[val] = true;
            values.push(val);
          }
        }
        if (values.length <= 1) continue;

        html += '<select class="tall-owl-ymal__select" data-option-index="' + i + '" aria-label="' + esc(optionName) + '">';
        for (var k = 0; k < values.length; k++) {
          html += '<option value="' + esc(values[k]) + '">' + esc(values[k]) + '</option>';
        }
        html += '</select>';
      }
      return html;
    }

    function findVariant(product, selectedOptions) {
      var variants = product.variants || [];
      for (var i = 0; i < variants.length; i++) {
        var match = true;
        for (var j = 0; j < selectedOptions.length; j++) {
          if ((variants[i]['option' + (j + 1)] || '') !== selectedOptions[j]) {
            match = false;
            break;
          }
        }
        if (match) return variants[i];
      }
      return variants[0] || null;
    }

    function buildCard(product) {
      var variant = product.variants && product.variants[0] ? product.variants[0] : null;
      if (!variant) return '';

      var imgSrc = product.featured_image
        ? product.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_240x240$1$2')
        : '';
      var available = variant.available !== false;
      var optionSelects = buildOptionSelects(product);

      return (
        '<div class="tall-owl-ymal__card" data-product-handle="' + esc(product.handle) + '" data-product-json=\'' + JSON.stringify(product.variants).replace(/'/g, '&#39;') + '\'  data-product-options=\'' + JSON.stringify(product.options || []).replace(/'/g, '&#39;') + '\'>' +
          '<a href="/products/' + esc(product.handle) + '" class="tall-owl-ymal__image-link">' +
            (imgSrc
              ? '<img class="tall-owl-ymal__image" src="' + imgSrc + '" alt="' + esc(product.title) + '" loading="lazy" width="120" height="120">'
              : '<div class="tall-owl-ymal__image" style="background:#eee"></div>') +
          '</a>' +
          '<div class="tall-owl-ymal__info">' +
            '<a href="/products/' + esc(product.handle) + '" class="tall-owl-ymal__title">' + esc(product.title) + '</a>' +
            '<span class="tall-owl-ymal__price" data-ymal-price>' + formatMoney(variant.price) + '</span>' +
          '</div>' +
          (optionSelects ? '<div class="tall-owl-ymal__options">' + optionSelects + '</div>' : '') +
          '<button type="button" class="tall-owl-ymal__add" data-variant-id="' + variant.id + '" data-product-handle="' + esc(product.handle) + '"' +
            (available ? '' : ' disabled') + '>' +
            (available ? 'ADD' : 'Sold out') +
          '</button>' +
        '</div>'
      );
    }

    function initArrows(widget) {
      var track = widget.querySelector('.tall-owl-ymal__track');
      var prev = widget.querySelector('.tall-owl-ymal__arrow--prev');
      var next = widget.querySelector('.tall-owl-ymal__arrow--next');
      if (!track || !prev || !next) return;

      function update() {
        var sl = Math.round(track.scrollLeft);
        var max = track.scrollWidth - track.clientWidth;
        prev.hidden = sl <= 2;
        next.hidden = sl >= max - 2;
      }

      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();

      prev.addEventListener('click', function () {
        track.scrollBy({ left: -280, behavior: 'smooth' });
      });
      next.addEventListener('click', function () {
        track.scrollBy({ left: 280, behavior: 'smooth' });
      });
    }

    /* Parse variant JSON once per card and cache on the DOM node to avoid
       repeated JSON.parse on rapid mobile select interactions. */
    function getCardVariants(card) {
      if (card._ymalVariantsCache) return card._ymalVariantsCache;
      var raw = card.getAttribute('data-product-json');
      if (!raw) return [];
      try { card._ymalVariantsCache = JSON.parse(raw); } catch (e) { card._ymalVariantsCache = []; }
      return card._ymalVariantsCache;
    }

    function resolveVariantFromCard(card) {
      var variants = getCardVariants(card);
      if (!variants.length) return null;

      var selects = card.querySelectorAll('.tall-owl-ymal__select');
      if (!selects.length) return variants[0];

      var selectedOptions = [];
      selects.forEach(function (s) { selectedOptions.push(s.value); });

      for (var i = 0; i < variants.length; i++) {
        var isMatch = true;
        for (var j = 0; j < selectedOptions.length; j++) {
          if ((variants[i]['option' + (j + 1)] || '') !== selectedOptions[j]) {
            isMatch = false;
            break;
          }
        }
        if (isMatch) return variants[i];
      }
      return variants[0];
    }

    function handleOptionChange(event) {
      var select = event.target.closest('.tall-owl-ymal__select');
      if (!select) return;

      var card = select.closest('.tall-owl-ymal__card');
      if (!card) return;

      var matched = resolveVariantFromCard(card);
      if (!matched) return;

      var btn = card.querySelector('.tall-owl-ymal__add');
      var priceEl = card.querySelector('[data-ymal-price]');

      if (btn) {
        btn.setAttribute('data-variant-id', matched.id);
        /* Don't overwrite button text while an add is in flight -
           the add handler owns the text during that window. */
        if (!btn.hasAttribute('data-adding')) {
          btn.disabled = !matched.available;
          btn.textContent = matched.available ? 'ADD' : 'Sold out';
        }
      }
      if (priceEl) {
        priceEl.textContent = formatMoney(matched.price);
      }
    }

    /* Serialize all YMAL adds so only one /cart/add.js is in flight at a time. */
    var ymalAddQueue = Promise.resolve();
    var ymalBusy = false;

    function handleAddClick(event) {
      var btn = event.target.closest('.tall-owl-ymal__add');
      if (!btn || btn.disabled || btn.hasAttribute('data-adding')) return;

      /* Block ALL YMAL add buttons while any add is in progress */
      if (ymalBusy) return;

      /* Read variant ID at click time, then immediately re-resolve from the
         current select values to guard against mobile browsers where the
         change event on <select> can fire slightly after a fast tap on ADD. */
      var card = btn.closest('.tall-owl-ymal__card');
      var resolved = card ? resolveVariantFromCard(card) : null;
      var variantId = resolved ? resolved.id : btn.getAttribute('data-variant-id');
      if (!variantId) return;

      /* If resolved variant is unavailable, bail */
      if (resolved && resolved.available === false) {
        btn.disabled = true;
        btn.textContent = 'Sold out';
        return;
      }

      ymalBusy = true;
      btn.setAttribute('data-adding', '');
      btn.textContent = 'Adding...';

      /* Freeze selects on this card so the variant can't change mid-request */
      var cardSelects = card ? card.querySelectorAll('.tall-owl-ymal__select') : [];
      cardSelects.forEach(function (s) { s.disabled = true; });

      ymalAddQueue = ymalAddQueue
        .then(function () {
          return addToCart(Number(variantId), 1);
        })
        .then(function () {
          btn.textContent = 'Added!';
          return new Promise(function (resolve) { setTimeout(resolve, 250); });
        })
        .then(function () {
          btn.textContent = 'ADD';
          btn.removeAttribute('data-adding');
          cardSelects.forEach(function (s) { s.disabled = false; });
          ymalBusy = false;
        })
        .catch(function () {
          btn.textContent = 'Error';
          return new Promise(function (resolve) {
            setTimeout(function () {
              btn.textContent = 'ADD';
              btn.removeAttribute('data-adding');
              cardSelects.forEach(function (s) { s.disabled = false; });
              ymalBusy = false;
              resolve();
            }, 1500);
          });
        });
    }

    function fetchAndRender(widget) {
      var track = widget.querySelector('.tall-owl-ymal__track');
      if (!track) return;

      widget.setAttribute('hidden', 'hidden');
      track.innerHTML = '';

      fetch('/cart.js', { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart.items || !cart.items.length) return null;

          var seedItem = cart.items[0];
          return seedItem && seedItem.product_id ? seedItem.product_id : null;
        })
        .then(function (productId) {
        if (!productId) { return; }
        widget.setAttribute('data-product-id', productId);

        var url = '/recommendations/products.json?product_id=' + productId + '&limit=10&intent=related';

        return fetch(url, { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Recommendations failed');
          return response.json();
        })
        .then(function (data) {
          var products = data && data.products ? data.products : [];
          if (!products.length) return;

          var excludeHandles = {};

          /* Filter out products already in cart */
          var cartItems = document.querySelectorAll('.cart__items [data-product-handle], .cart__items [data-id]');
          cartItems.forEach(function (el) {
            var h = (el.getAttribute('data-product-handle') || '').toLowerCase();
            if (h) excludeHandles[h] = true;
          });

          document.querySelectorAll('[data-products] a[href*="/products/"]').forEach(function (a) {
            var match = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
            if (match && match[1]) excludeHandles[decodeURIComponent(match[1]).toLowerCase()] = true;
          });

          var filtered = products.filter(function (p) {
            return !excludeHandles[p.handle.toLowerCase()];
          });

          if (!filtered.length) return;

          var html = '';
          filtered.forEach(function (p) {
            html += buildCard(p);
          });
          track.innerHTML = html;

          widget.removeAttribute('hidden');
          initArrows(widget);
        })
        .catch(function () {
          /* Silently fail - recommendations are non-critical */
        });
        })
        .catch(function () {
          /* Cart fetch or product ID resolution failed - non-critical */
        });
    }

    function bindWidget(widget) {
      if (!widget || widget.getAttribute('data-tall-owl-ymal-bound') === 'true') return;
      widget.setAttribute('data-tall-owl-ymal-bound', 'true');
      widget.addEventListener('click', handleAddClick);
      widget.addEventListener('change', handleOptionChange);
    }

    function refreshWidgets() {
      var ymalWidgets = document.querySelectorAll('[data-tall-owl-ymal]');
      if (!ymalWidgets.length) return;

      ymalWidgets.forEach(function (widget) {
        bindWidget(widget);
        fetchAndRender(widget);
      });
    }

    function scheduleRefresh() {
      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
      }

      refreshTimer = window.setTimeout(function () {
        refreshTimer = null;
        refreshWidgets();
      }, 180);
    }

    document.addEventListener('ajaxProduct:added', scheduleRefresh);
    document.addEventListener('cart:updated', scheduleRefresh);
    document.addEventListener('cart:build', scheduleRefresh);
    document.addEventListener('drawerOpen.CartDrawer', scheduleRefresh);

    refreshWidgets();
  })();
  // ---------- End Cart YMAL ----------

  applyModalAccessibility();
})();
/* ==========================================================================
   Tall Owl scroll reveal - gentle fade + rise as sections enter the viewport.
   - No-op if IntersectionObserver is unsupported or reduced motion is preferred.
   - Content above the fold is never hidden (no load flash).
   - Footer/header are untouched; only .tall-owl-section content reveals.
   ========================================================================== */
(function () {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var docEl = document.documentElement;
  var STAGGER = 90; // ms between staggered items
  var MAX_STEPS = 6; // cap so long lists never feel slow
  var GRID_SELECTOR = '.tall-owl-grid, .tall-owl-bestsellers';
  var observer;

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    return observer;
  }

  /* rev 2026-06-10b: hero excluded from reveal (LCP fix) */
  function collectSection(section, out) {
    // Never animate the hero: it holds the LCP image, and on slow loads the
    // above-fold check can misjudge it mid-layout, hiding the LCP for ~2s
    // (the source of the bimodal 15s mobile LCP in Lighthouse/PSI).
    if (section.classList && section.classList.contains('tall-owl-hero')) return;
    var container = section.querySelector('.tall-owl-container') || section;
    var items = Array.prototype.slice.call(container.children);
    // If one wrapper holds everything, step inside it so the children cascade.
    if (items.length === 1 && items[0].children && items[0].children.length > 1) {
      items = Array.prototype.slice.call(items[0].children);
    }
    items.forEach(function (item, i) {
      if (item.matches && item.matches(GRID_SELECTOR)) {
        Array.prototype.slice.call(item.children).forEach(function (card, ci) {
          if (card && card.nodeType === 1 && !card.classList.contains('to-reveal')) {
            out.push({ el: card, index: ci });
          }
        });
      } else if (item && item.nodeType === 1 && !item.classList.contains('to-reveal')) {
        out.push({ el: item, index: i });
      }
    });
  }

  function applyReveal(candidates) {
    if (!candidates.length) return;
    // Batch every geometry read before any class/style write - interleaving the
    // two forces a synchronous reflow per element (flagged by Lighthouse).
    var foldLine = window.innerHeight * 0.88;
    var tops = candidates.map(function (c) {
      return c.el.getBoundingClientRect().top;
    });
    candidates.forEach(function (c, i) {
      // Leave anything already at/above the fold visible - animate only what's below it.
      if (tops[i] < foldLine) return;
      c.el.style.setProperty('--to-delay', (Math.min(c.index, MAX_STEPS) * STAGGER) + 'ms');
      c.el.classList.add('to-reveal');
      getObserver().observe(c.el);
    });
  }

  function revealSection(section) {
    var candidates = [];
    collectSection(section, candidates);
    applyReveal(candidates);
  }

  function init(scope) {
    var root = scope || document.querySelector('.main-content') || document;
    var sections = root.querySelectorAll('.tall-owl-section');
    if (!sections.length) return;
    docEl.classList.add('to-reveal-ready');
    var candidates = [];
    Array.prototype.forEach.call(sections, function (section) {
      collectSection(section, candidates);
    });
    applyReveal(candidates);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Re-arm sections re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !target.querySelectorAll) return;
    docEl.classList.add('to-reveal-ready');
    if (target.classList && target.classList.contains('tall-owl-section')) {
      revealSection(target);
    }
    Array.prototype.forEach.call(target.querySelectorAll('.tall-owl-section'), revealSection);
  });
})();
